/* ============================================================================
   Merchloop store request wizard, core.

   Welcome gate, then a branching flow:
     Essential      -> Details, Brand, Plan, Products, Design, Review
     anything above -> Details, Brand, Plan, Tell us more  (a sales lead)

   Step 4 of the self serve path is handed to mlsr-catalog.js, which embeds the
   real SwagOS browse UI.

   The product selection is shared with the floating request tray and the Bulk
   Order product template through the `mlbStoreRequest` localStorage key.

   Submission goes through HubSpot's embedded form, the same mechanism
   snippets/mlb-request-tray.liquid uses, because that is the only path that can
   carry the logo file. Falls back to Shopify's contact form.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__mlsrWizard) return;
  window.__mlsrWizard = true;

  var TRAY_KEY = 'mlbStoreRequest';
  var DRAFT_KEY = 'mlsrDraft:v3';
  var MAX_LOGO_MB = 9;
  // Up to five logos can be uploaded, one chosen per product on the Design step.
  var MAX_LOGOS = 5;
  // Why a customer would add a second logo. Each slot says what it is for, so
  // the reason is on the screen rather than in our heads. 'main' is the one
  // upload that is required. The names here become the chips on the Design
  // step and the labels on the ticket, so they are written to be read there.
  var LOGO_SLOTS = [
    { k: 'light', title: 'White or light version', name: 'Light version', reason: 'for dark colored items',
      desc: 'For dark colored items like black, navy or charcoal, where your main logo would disappear.' },
    { k: 'mark', title: 'Logo mark only', name: 'Logo mark only', reason: 'icon without the wordmark',
      desc: 'Just the icon or symbol, for small spots like a sleeve, cap side or pocket.' },
    { k: 'alt', title: 'Stacked or horizontal version', name: 'Alternate layout', reason: 'alternate layout',
      desc: 'A different layout for a spot that is too narrow or too wide for the main one.' },
    { k: 'single', title: 'Single color version', name: 'Single color version', reason: 'one flat color, for laser engraving and drinkware',
      desc: 'Your logo in one flat color. The best choice for drinkware and anything laser engraved, since it finishes as a single color. Also the one to use for embroidery if your main logo has a gradient.' }
  ];
  var SLOT_ORDER = ['main', 'light', 'mark', 'alt', 'single'];
  function slotInfo(k) {
    for (var i = 0; i < LOGO_SLOTS.length; i++) if (LOGO_SLOTS[i].k === k) return LOGO_SLOTS[i];
    return null;
  }
  // Kevin's bands, 17 September. They map to the HubSpot contact property
  // `company_size`, which is a free text string rather than an enumeration, so
  // these exact labels store as written. Deliberately NOT `numemployees`, whose
  // fixed options (1-5, 5-25, 25-50, 50-100, 100-500, 500-1000, 1000+) do not
  // line up: a value outside an enumeration's options is dropped silently, the
  // same trap that once made store_type_requested arrive empty.
  var COMPANY_SIZES = ['', '1-49', '50-99', '100-249', '250-499', '500-999',
                       '1,000-2,499', '2,500-9,999', '10K+'];
  // Elements inside a step that own a scroll position of their own. Rebuilding
  // the step's markup destroys them, and with them wherever the customer had
  // scrolled to, so render() saves and restores these by hand.
  var SCROLLERS = ['.mlsr-dlist', '.mlsr-picks__list', '.mlsr-review__items'];
  // Guardrails, not a bug fix. A free Essential store with two hundred
  // colorways is not something we can actually stand up, and the Design step
  // becomes unreadable long before that. Both are enforced in the picker and
  // on the colorway chips, so nobody builds a basket the form then refuses.
  var MAX_PRODUCTS = 20;
  var MAX_COLORS = 7;
  // Referral attribution, 1 October. The roster of reps lives in the section
  // setting, not here, because who is on the team changes faster than we
  // deploy, and a stale name in a commission field is worse than no field.
  // This sentinel is a real choice rather than an empty string, so "someone
  // else, let me type it" stays distinguishable from "I did not answer".
  var REFERRAL_OTHER = '__other__';

  var I = {
    check: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
    chevR: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>',
    chevL: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>',
    chevU: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>',
    chevD: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>',
    x: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    plus: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
    copy: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>',
    spark: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"/></svg>',
    info: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
    up: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>',
    clock: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
    bolt: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>',
    chat: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
    cal: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>'
  };

  var LOGO_STYLES = [
    { v: 'Horizontal', l: 'Horizontal' },
    { v: 'Stacked', l: 'Stacked / Vertical' },
    { v: 'Icon/Mark', l: 'Icon / Mark' }
  ];
  var ACCEPT = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp',
    'application/pdf', 'application/postscript', 'application/illustrator', 'application/eps'];

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function scrollTop() {
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { window.scrollTo(0, 0); }
  }
  function uniq(a) {
    var seen = {}, out = [];
    a.forEach(function (x) { if (x && !seen[x]) { seen[x] = 1; out.push(x); } });
    return out;
  }

  function Wizard(root) {
    var cfg = JSON.parse(root.querySelector('[data-mlsr-config]').textContent);
    var SELF_SERVE = cfg.self_serve_tier || 'Essential';

    var state = {
      started: false,
      step: 1,
      data: {
        companyName: '', website: '', companySize: '',
        referralRep: '', referralOther: '',
        firstName: '', lastName: '', email: '', phone: '',
        logoSource: '', logoFoundUrl: '',
        storeTier: '', products: [],
        salesHeadcount: '', salesTimeline: '', salesNotes: ''
      },
      // Uploaded logos, in the order added: { id, file, name, previewUrl }.
      // Files cannot be drafted to storage, so this resets on a reload.
      logos: [],
      // Set at send time: which logos could not ride along on the ticket, so
      // the ticket can say so rather than claiming an attachment that is not there.
      unattached: {},
      noFiles: false,
      hsError: '',
      capNote: '',
      colorCap: {},
      errors: {},
      submitting: false,
      syncing: false,
      // Transient, deliberately not in the draft: whether the picked list is
      // expanded, and whether Clear all is armed for its second click.
      picksOpen: false,
      clearArmed: false,
      // Which products have their print detail panel open on step 5, keyed by
      // index. Collapsed is the default, because the defaults are already right.
      openPrint: {},
      // Which tier cards have their full feature list expanded, keyed by tier id.
      openFeats: {},
      logoFound: null,
      attachedLogo: null
    };
    var logoSeq = 0;
    var logoUid = 0;

    var elSteps = root.querySelector('[data-mlsr-steps]');
    var elTrack = root.querySelector('[data-mlsr-track]');
    var elDraft = root.querySelector('[data-mlsr-draft]');

    function isSales() { return !!state.data.storeTier && state.data.storeTier !== SELF_SERVE; }
    function stepNames() {
      return isSales()
        ? ['Your details', 'Your brand', 'Your plan', 'Tell us more']
        : ['Your details', 'Your brand', 'Your plan', 'Products', 'Design', 'Review'];
    }
    function lastStep() { return stepNames().length; }

    /* -- the shared mlbStoreRequest list ---------------------------------- */

    function trayRead() {
      try {
        var v = JSON.parse(localStorage.getItem(TRAY_KEY) || '[]');
        return Array.isArray(v) ? v : [];
      } catch (e) { return []; }
    }
    function trayWrite(rows) {
      state.syncing = true;
      try { localStorage.setItem(TRAY_KEY, JSON.stringify(rows)); } catch (e) {}
      try { document.dispatchEvent(new CustomEvent('mlb:req-updated')); } catch (e) {}
      setTimeout(function () { state.syncing = false; }, 0);
    }
    function exportToTray() {
      var rows = [];
      state.data.products.forEach(function (p) {
        var colors = p.colorsRequested
          ? p.colorsRequested.split(/,\s*/).map(function (s) { return s.trim(); }).filter(Boolean)
          : [''];
        colors.forEach(function (c, i) {
          rows.push({
            h: p.h, title: p.title, color: c, img: p.img,
            mlsr: i === 0 ? {
              url: p.url, cat: p.cat, brand: p.brand, deco: p.deco,
              decosrc: p.decosrc, spots: p.spots, available: p.available,
              logoPlacement: p.logoPlacement, logoStyle: p.logoStyle,
              additionalPlacements: p.additionalPlacements
            } : undefined
          });
        });
      });
      trayWrite(rows);
    }
    function importFromTray() {
      var rows = trayRead(), byHandle = {}, order = [];
      rows.forEach(function (r) {
        if (!r || !r.h) return;
        if (!byHandle[r.h]) { byHandle[r.h] = { colors: [], extra: null }; order.push(r.h); }
        if (r.color) byHandle[r.h].colors.push(r.color);
        if (r.mlsr && !byHandle[r.h].extra) byHandle[r.h].extra = r.mlsr;
      });
      var existing = {};
      state.data.products.forEach(function (p) { existing[p.h] = p; });
      state.data.products = order.map(function (h) {
        var prev = existing[h], x = byHandle[h].extra || {};
        var colors = uniq(byHandle[h].colors);
        var meta = rows.filter(function (r) { return r.h === h; })[0] || {};
        return applyDefaults({
          h: h,
          title: (prev && prev.title) || meta.title || h,
          img: (prev && prev.img) || meta.img || '',
          url: (prev && prev.url) || x.url || ('/products/' + h),
          cat: (prev && prev.cat) || x.cat || '',
          brand: (prev && prev.brand) || x.brand || '',
          deco: (prev && prev.deco) || x.deco || '',
          decosrc: (prev && prev.decosrc) || x.decosrc || 'guess',
          spots: (prev && prev.spots) || x.spots || [],
          available: (prev && prev.available) || x.available || colors,
          colorsRequested: colors.join(', '),
          logoPlacement: (prev && prev.logoPlacement) || x.logoPlacement || '',
          logoStyle: (prev && prev.logoStyle) || x.logoStyle || '',
          additionalPlacements: (prev && prev.additionalPlacements) || x.additionalPlacements || [],
          logoId: (prev && prev.logoId) || '',
          blank: !!((prev && prev.blank) || (x && x.blank) || meta.blank)
        });
      });
    }
    function onExternalChange() {
      if (state.syncing) return;
      importFromTray();
      saveDraft();
      notifySelection();
      if (state.step === 4 && !isSales()) updateSelBar();
      if (state.step >= 5) render();
    }
    document.addEventListener('mlb:req-updated', onExternalChange);
    window.addEventListener('storage', function (ev) {
      if (!ev.key || ev.key === TRAY_KEY) onExternalChange();
    });

    var selectionListeners = [];
    function notifySelection() { selectionListeners.forEach(function (f) { f(); }); }

    /* -- draft ------------------------------------------------------------ */

    function saveDraft() {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({
          t: Date.now(), step: state.step, started: state.started, data: state.data
        }));
      } catch (e) {}
    }
    function clearDraft() { try { localStorage.removeItem(DRAFT_KEY); } catch (e) {} }
    function readDraft() {
      try {
        var d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
        if (!d || !d.data) return null;
        if (Date.now() - (d.t || 0) > 1000 * 60 * 60 * 24 * 14) return null;
        if (!d.data.companyName && !(d.data.products || []).length) return null;
        return d;
      } catch (e) { return null; }
    }

    /* -- selection -------------------------------------------------------- */

    function isSelected(h) { return state.data.products.some(function (p) { return p.h === h; }); }
    function toggleProduct(p) {
      var i = -1;
      state.data.products.forEach(function (x, k) { if (x.h === p.h) i = k; });
      if (i > -1) state.data.products.splice(i, 1);
      else {
        // Refuse rather than silently drop it later. Removing one still works,
        // so the customer is never stuck.
        if (state.data.products.length >= MAX_PRODUCTS) {
          state.capNote = 'You can request up to ' + MAX_PRODUCTS + ' products at a time. ' +
            'Remove one to swap it, or send this request and we will add the rest with you.';
          notifySelection(); updateSelBar();
          return;
        }
        var item = {
          h: p.h, title: p.title, img: p.img, url: p.url,
          cat: p.cat, brand: p.brand, deco: p.deco,
          decosrc: p.decosrc || 'guess',
          spots: (p.spots || []).slice(),
          available: (p.colors || []).slice(),
          colorsRequested: (p.colors && p.colors[0]) || '',
          // additionalPlacements is always empty now that a product decorates
          // in exactly one place. The field stays because the request tray
          // snippet reads it, and a missing key there would be a harder bug to
          // find than an empty array is to ignore.
          logoPlacement: '', logoStyle: '', additionalPlacements: [],
          blank: !!p.blank
        };
        applyDefaults(item);
        state.data.products.push(item);
      }
      state.capNote = '';
      delete state.errors.products;
      exportToTray(); saveDraft(); notifySelection(); updateSelBar();
    }
    /* Every product arrives already set up the standard way, so the Design step
       is somewhere you change things rather than somewhere you fill things in.
       Nothing on that step is required as a result. */
    function applyDefaults(item) {
      if (!item.colorsRequested) item.colorsRequested = (item.available && item.available[0]) || '';
      if (item.blank) { item.logoPlacement = ''; item.logoStyle = ''; return item; }
      if (!item.logoPlacement) item.logoPlacement = placementsFor(item)[0] || '';
      item.logoStyle = '';
      return item;
    }
    // Where a logo can actually go is a property of the product, not of its
    // category. A Carhartt jacket takes Right Chest and Left Sleeve; the
    // category map offered every jacket the same generic three, so customers
    // were picking locations we cannot print and our team was correcting them
    // afterwards. merchloop.decoration_spots carries the real answer and is
    // populated across effectively the whole catalog, so it wins outright.
    // The category map stays as the fallback for anything that arrives without
    // it, including drafts saved before this shipped.
    function placementsFor(item) {
      if (item && item.spots && item.spots.length) return item.spots;
      var m = cfg.placements || {};
      return (m[item.cat] && m[item.cat].length)
        ? m[item.cat]
        : (cfg.default_placements || ['Left Chest', 'Full Front Chest', 'Full Back']);
    }

    /* -- validation ------------------------------------------------------- */

    function validateStep(n) {
      var e = {}, d = state.data;
      if (n === 1) {
        if (!d.companyName.trim()) e.companyName = 'Company name is required';
        if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+/i.test(d.website.trim().replace(/^https?:\/\//i, ''))) {
          e.website = 'Please enter your website, for example acme.com';
        }
        if (!d.companySize) e.companySize = 'Please choose a company size';
        if (!d.firstName.trim()) e.firstName = 'First name is required';
        if (!d.lastName.trim()) e.lastName = 'Last name is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(d.email.trim())) e.email = 'Please enter a valid email address';
        if (d.phone.replace(/\D/g, '').length < 7) e.phone = 'Please enter a valid phone number';
      }
      // The main logo is required, with no way round it: nothing can be mocked
      // up without artwork. If the logo finder pulled something and they
      // accepted it, that counts as the main logo.
      if (n === 2 && !haveArtwork() && !allProductsBlank()) {
        e.logoFile = state.logoFound
          ? 'Please upload your main logo, or use the one we found on your site'
          : 'Please upload your main logo so we can mock your products up';
      }
      if (n === 3 && !d.storeTier) e.storeTier = 'Please select a store tier';
      if (!isSales()) {
        if (n === 4 && !d.products.length) e.products = 'Please add at least one product to your store';
        // Everything on this step arrives pre-filled with our standard setup, so
        // the only thing that can go wrong is someone clearing every colorway.
        if (n === 5) {
          d.products.forEach(function (p, i) {
            if (!p.colorsRequested.trim()) e['p' + i + '.colorsRequested'] = 'Please keep at least one color';
          });
        }
      }
      state.errors = e;
      return !Object.keys(e).length;
    }

    /* -- navigation ------------------------------------------------------- */

    function goTo(n) {
      // Uploaded files cannot be kept in the saved draft, so someone who comes
      // back via "Pick up where I left off" has their details but no logo. No
      // step past the brand step is reachable without artwork, whatever route
      // got them there (draft resume, back button, tracker).
      var lostLogo = false;
      if (n > 2 && state.started && !haveArtwork() && !allProductsBlank()) { n = 2; lostLogo = true; }
      state.step = n;
      state.errors = {};
      if (lostLogo) state.errors.logoFile = 'Your logo was not saved from your last visit. Please upload your main logo again to continue.';
      state.picksOpen = false;
      state.clearArmed = false;
      root.setAttribute('data-step', n);
      root.setAttribute('data-mode', isSales() ? 'sales' : 'self');
      saveDraft();
      render();
      var node = elSteps.firstElementChild;
      if (node) {
        node.classList.add('is-entering');
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            node.classList.remove('is-entering');
            node.classList.add('is-settled');
          });
        });
      }
      scrollTop();
    }
    function next() {
      if (!validateStep(state.step)) { render(); focusFirstError(); return; }
      if (state.step < lastStep()) goTo(state.step + 1);
    }
    function back() {
      if (state.step > 1) goTo(state.step - 1);
      else { state.started = false; saveDraft(); render(); scrollTop(); }
    }
    function focusFirstError() {
      var bad = root.querySelector('.is-invalid input, .is-invalid select, .mlsr-dcard.is-invalid');
      if (bad) {
        if (bad.scrollIntoView) bad.scrollIntoView({ block: 'center', behavior: 'smooth' });
        try { bad.focus({ preventScroll: true }); } catch (e) {}
      }
    }

    /* -- render ----------------------------------------------------------- */

    function renderTracker() {
      if (!state.started) { elTrack.innerHTML = ''; elTrack.hidden = true; return; }
      elTrack.hidden = false;
      var names = stepNames();
      elTrack.innerHTML = '<ol class="mlsr-track">' + names.map(function (n, i) {
        var idx = i + 1;
        var cls = idx < state.step ? 'is-done' : (idx === state.step ? 'is-now' : '');
        return '<li class="mlsr-track__step ' + cls + '">' +
          '<span class="mlsr-track__dot">' + (idx < state.step ? I.check : idx) + '</span>' +
          '<span class="mlsr-track__label">' + esc(n) + '</span></li>';
      }).join('') + '</ol>';
    }

    function render() {
      renderTracker();
      var html;
      if (!state.started) html = welcome();
      else if (state.step === 1) html = step1();
      else if (state.step === 2) html = step2();
      else if (state.step === 3) html = step3();
      else if (isSales()) html = stepSales();
      else if (state.step === 4) html = step4();
      else if (state.step === 5) html = step5();
      else html = step6();
      // Replacing the step's innerHTML tears down and rebuilds every node, and
      // while the document is momentarily shorter the browser clamps the scroll
      // position to the new height. The result was that opening a print panel
      // on the eighth product threw you back to the top of the page, every
      // time. Restore the offset synchronously, before paint, so the swap is
      // invisible. Step changes still scroll to the top deliberately: goTo()
      // calls scrollTop() after this returns, and that call still wins.
      var keepY = window.pageYOffset || document.documentElement.scrollTop || 0;
      // The design step's product list is its own scroll container, so it has
      // its own scroll position to lose, and losing that is what actually threw
      // you back to the first product every time you touched anything on the
      // eighth. Capture both, restore both.
      var inner = {};
      SCROLLERS.forEach(function (sel) {
        var el = elSteps.querySelector(sel);
        if (el && el.scrollTop) inner[sel] = el.scrollTop;
      });

      elSteps.innerHTML = '<div class="mlsr__step is-active">' + html + '</div>';

      Object.keys(inner).forEach(function (sel) {
        var el = elSteps.querySelector(sel);
        if (el) el.scrollTop = Math.min(inner[sel], Math.max(0, el.scrollHeight - el.clientHeight));
      });
      if (keepY) {
        var maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        window.scrollTo(0, Math.min(keepY, maxY));
      }
      if (state.started && state.step === 4 && !isSales()) mountPicker();
      if (state.started && state.step === 2) tryLogoPreview();
    }

    function err(k) { return state.errors[k] ? '<p class="mlsr-err">' + esc(state.errors[k]) + '</p>' : ''; }
    function fcls(k) { return 'mlsr-field' + (state.errors[k] ? ' is-invalid' : ''); }
    function foot(label, plain) {
      return '<div class="mlsr__foot' + (plain ? ' mlsr__foot--plain' : '') + '">' +
        '<button type="button" class="mlsr-btn mlsr-btn--outline" data-act="back">' + I.chevL + ' Back</button>' +
        '<button type="button" class="mlsr-btn mlsr-btn--primary" data-act="next">' + label + ' ' + I.chevR + '</button>' +
        '</div>';
    }
    function helpBar() {
      var bits = [];
      if (cfg.help_meeting_url) {
        // A real href, so it still works without JS and can be opened in a new
        // tab on purpose, but a click keeps them inside the wizard.
        bits.push('<a class="mlsr-help__a" href="' + esc(cfg.help_meeting_url) + '" target="_blank" rel="noopener" ' +
          'data-act="book">' + I.cal + ' Book a 15 minute call</a>');
      }
      bits.push('<button type="button" class="mlsr-help__a" data-act="chat">' + I.chat + ' Chat with us</button>');
      return '<div class="mlsr-help"><span class="mlsr-help__t">Stuck, or want a hand?</span>' + bits.join('') + '</div>';
    }

    /* ---- welcome --------------------------------------------------------- */

    function welcome() {
      var names = ['Your details', 'Your brand', 'Your plan', 'Products', 'Design', 'Review'];
      var blurbs = [
        'Company, website and how to reach you.',
        'Your logo, or we pull it straight from your site.',
        'Pick the store tier that fits your team.',
        'Choose what you want to sell from the Merchloop catalog.',
        'Colorways and print locations for each item.',
        'Check it over and send it to us.'
      ];
      var hues = ['teal', 'navy', 'amber', 'red'];
      var tiles = names.map(function (n, i) {
        return '<li class="mlsr-feat mlsr-feat--' + hues[i % hues.length] + '">' +
          '<span class="mlsr-feat__art"><span class="mlsr-feat__n">' + (i + 1) + '</span></span>' +
          '<span class="mlsr-feat__t"><b>' + esc(n) + '</b>' +
            '<span class="mlsr-feat__rule"></span>' +
            '<em>' + esc(blurbs[i]) + '</em>' +
          '</span>' +
        '</li>';
      }).join('');

      // What you are promising sits on the left with the button, what the job
      // involves sits on the right. Stripe's Connect setup and Mintlify's first
      // run both do this, and it is what gets the button above the fold.
      var meta = [
        [I.clock, 'About ten minutes, and you can leave and come back without losing your place.'],
        [I.check, 'Nothing is ordered and nothing is charged. We build a preview first.'],
        [I.chat, 'Stuck at any point? Book a call or chat with us from any step.']
      ].map(function (r) {
        return '<li>' + r[0] + '<span>' + r[1] + '</span></li>';
      }).join('');

      return '<div class="mlsr-welcome">' +
        '<div class="mlsr-welcome__cols">' +
          '<div class="mlsr-welcome__pitch">' +
            '<p class="mlsr-welcome__eyebrow">' + esc(cfg.welcome_eyebrow || 'Start your store') + '</p>' +
            '<h2>' + esc(cfg.welcome_heading || 'Let\'s build your company store') + '</h2>' +
            '<p class="mlsr__sub mlsr__sub--lg">' + esc(cfg.welcome_body ||
              'Six short steps, and you end up with a company store built around your brand.') + '</p>' +
            '<ul class="mlsr-welcome__meta">' + meta + '</ul>' +
            '<div class="mlsr-welcome__cta">' +
              '<button type="button" class="mlsr-btn mlsr-btn--primary mlsr-btn--hero" data-act="start">' +
                esc(cfg.welcome_cta || 'Start my store') + ' ' + I.chevR + '</button>' +
            '</div>' +
            '<div class="mlsr-welcome__need">' + I.info +
              '<div><b>What to have handy</b>' +
              '<ul><li>Your logo file. A vector (AI, EPS or SVG) is ideal, but PNG or JPG works. A white version for dark items is a bonus.</li>' +
              '<li>Gradients are not accepted for embroidered items, so if your logo has one, have a flat, solid color version ready.</li>' +
              '<li>A rough idea of how many people the store is for.</li>' +
              '<li>A sense of which products you want on the store.</li></ul></div>' +
            '</div>' +
          '</div>' +
          '<div class="mlsr-welcome__right">' +
            '<p class="mlsr-welcome__st">What we go through</p>' +
            '<ol class="mlsr-welcome__steps mlsr-feats">' + tiles + '</ol>' +
          '</div>' +
        '</div>' +
        helpBar() +
      '</div>';
    }

    /* ---- shared two column frame ----------------------------------------- */

    // Steps 1, 2 and the sales branch are short forms. The shell is one fixed
    // width across every step, so those forms sit in the right hand column with
    // the heading and context in a left rail rather than stretching to 1240px.
    function aside(title, sub, notes) {
      return '<div class="mlsr-2col__aside">' +
        '<div class="mlsr__head"><h2>' + title + '</h2>' +
        '<p class="mlsr__sub">' + sub + '</p></div>' +
        (notes && notes.length
          ? '<ul class="mlsr-aside__notes">' + notes.map(function (n) {
              return '<li>' + I.check + '<span>' + n + '</span></li>';
            }).join('') + '</ul>'
          : '') +
      '</div>';
    }
    function twoCol(asideHtml, mainHtml) {
      return '<div class="mlsr-2col">' + asideHtml +
        '<div class="mlsr-2col__main">' + mainHtml + '</div></div>';
    }

    /* ---- step 1 ---------------------------------------------------------- */

    // The reps come from the section setting, one name per line. An empty list
    // is not a broken list: the field falls back to a plain name box, so the
    // question still gets asked and the answer still reaches the ticket on the
    // day this ships, before anyone has typed a roster into the theme editor.
    function referralReps() {
      return (cfg.referral_reps || []).filter(function (n) { return n && String(n).trim(); });
    }
    // The one value worth attributing. A picked name wins, because it came off
    // the roster and reconciles to a person; a typed one is trimmed so that
    // "  Jane Doe " and "Jane Doe" do not become two people to pay.
    function referralName() {
      var d = state.data;
      if (d.referralRep && d.referralRep !== REFERRAL_OTHER) return d.referralRep;
      return String(d.referralOther || '').trim();
    }

    function step1() {
      var d = state.data;
      function f(id, key, label, ph, type, extra) {
        return '<div class="' + fcls(key) + '">' +
          '<label class="mlsr-label" for="' + id + '">' + label + '<span class="req">*</span></label>' +
          '<input class="mlsr-input" id="' + id + '" type="' + type + '" placeholder="' + ph + '" value="' +
          esc(d[key]) + '" data-bind="' + key + '"' + (extra || '') + '>' + err(key) + '</div>';
      }
      // Deliberately optional, and deliberately not marked required. Most
      // people arrive without having spoken to anyone, and a required field
      // here would make them invent a name, which is the one answer that is
      // actively worse than silence when the output pays commission.
      function refField() {
        var reps = referralReps();
        var typed = !reps.length || d.referralRep === REFERRAL_OTHER;
        var sel = '';
        if (reps.length) {
          sel = '<select class="mlsr-select" id="mlsr-ref" data-bind="referralRep" data-mlsr-refsel>' +
            '<option value=""' + (d.referralRep ? '' : ' selected') + '>No, I found Merchloop on my own</option>' +
            reps.map(function (n) {
              var v = String(n).trim();
              return '<option value="' + esc(v) + '"' + (d.referralRep === v ? ' selected' : '') + '>' +
                esc(v) + '</option>';
            }).join('') +
            '<option value="' + REFERRAL_OTHER + '"' + (d.referralRep === REFERRAL_OTHER ? ' selected' : '') +
              '>Someone else on the team</option>' +
          '</select>';
        }
        return '<div class="mlsr-field">' +
          '<label class="mlsr-label" for="' + (reps.length ? 'mlsr-ref' : 'mlsr-refother') + '">' +
            'Has anyone at Merchloop been helping you?</label>' +
          sel +
          '<div data-mlsr-refother' + (typed ? '' : ' hidden') +
            (reps.length ? ' style="margin-top:10px"' : '') + '>' +
            '<input class="mlsr-input" id="mlsr-refother" type="text" placeholder="Their name" value="' +
              esc(d.referralOther) + '" data-bind="referralOther">' +
          '</div>' +
          '<p class="mlsr-hint">If you have been talking to someone we want to make sure they get credit and a high five!</p>' +
        '</div>';
      }
      return twoCol(
        aside('Your details', 'So we know who the store is for and how to reach you.', [
          'Your website is how we pull your logo, so you may not have to upload a thing.',
          'We only use your phone number if something needs a quick answer.',
          'Nothing is shared outside Merchloop.'
        ]),
        '<div class="mlsr-fields">' +
          f('mlsr-company', 'companyName', 'Company Name', 'Acme Corp', 'text', ' autocomplete="organization"') +
          '<div class="' + fcls('website') + '">' +
            '<label class="mlsr-label" for="mlsr-website">Company Website<span class="req">*</span></label>' +
            '<input class="mlsr-input" id="mlsr-website" type="text" placeholder="acme.com" value="' + esc(d.website) + '" data-bind="website" autocomplete="url">' +
            '<p class="mlsr-hint">We use it to pull your logo, so you may not need to upload anything.</p>' +
            err('website') +
          '</div>' +
          // Sits with the other company facts rather than down with the personal
          // ones, because it is a fact about the company, not about them.
          '<div class="' + fcls('companySize') + '">' +
            '<label class="mlsr-label" for="mlsr-size">Company Size<span class="req">*</span></label>' +
            '<select class="mlsr-select" id="mlsr-size" data-bind="companySize">' +
              COMPANY_SIZES.map(function (o) {
                return '<option value="' + esc(o) + '"' + (d.companySize === o ? ' selected' : '') + '>' +
                  (o || 'Select a range') + '</option>';
              }).join('') +
            '</select>' +
            '<p class="mlsr-hint">Roughly how many people work there. It tells us what to plan for.</p>' +
            err('companySize') +
          '</div>' +
          refField() +
          '<div class="mlsr-row mlsr-row--2">' +
            f('mlsr-fn', 'firstName', 'First Name', 'Jane', 'text', ' autocomplete="given-name"') +
            f('mlsr-ln', 'lastName', 'Last Name', 'Doe', 'text', ' autocomplete="family-name"') +
          '</div>' +
          '<div class="mlsr-row mlsr-row--2">' +
            f('mlsr-em', 'email', 'Work Email', 'jane@acme.com', 'email', ' autocomplete="email"') +
            f('mlsr-ph', 'phone', 'Phone Number', '(555) 123-4567', 'tel', ' autocomplete="tel"') +
          '</div>' +
        '</div>' + foot('Continue')
      ) + helpBar();
    }

    /* ---- step 2 ---------------------------------------------------------- */

    function step2() {
      var d = state.data;
      var host = siteHost();
      var found = host
        ? '<div class="mlsr-logopick" data-mlsr-logopreview hidden>' +
            '<button type="button" class="mlsr-logopick__opt" data-act="use-found" aria-pressed="' + (d.logoSource === 'site' ? 'true' : 'false') + '">' +
              '<img alt="" data-mlsr-logoimg>' +
              '<span><b data-mlsr-logotitle>Use this one</b><em>Found on ' + esc(host) + '</em>' +
              '<em class="mlsr-logopick__meta" data-mlsr-logometa></em></span>' +
            '</button>' +
          '</div>' +
          // When the finder comes back with nothing, say so. Showing nothing at
          // all reads as "you have no logo", when the truth is usually that
          // their firewall would not let us look.
          '<p class="mlsr-logomiss" data-mlsr-logomiss hidden></p>'
        : '';
      // Show the artwork, not just its filename. Seeing "Bulkswag Package
      // copy.webp" tells you nothing about whether you picked the right file;
      // seeing the image tells you instantly.
      var ACCEPT_ATTR = '.png,.jpg,.jpeg,.gif,.svg,.webp,.pdf,.ai,.eps';
      // Show the artwork, not just its filename: seeing the image tells you
      // instantly whether you picked the right file.
      function logoRow(l) {
        var thumb = l.previewUrl
          ? '<img class="mlsr-logofile__img" src="' + esc(l.previewUrl) + '" alt="">'
          // AI, EPS and PDF cannot be drawn by a browser, so name the format
          // rather than showing a broken image.
          : '<span class="mlsr-logofile__ext">' + esc(fileExt(l.file.name).toUpperCase() || 'FILE') + '</span>';
        var label = l.slot === 'main'
          ? '<span class="mlsr-logofile__n">Main logo</span>'
          : '<input class="mlsr-input mlsr-input--sm mlsr-logofile__name" type="text" maxlength="40" value="' + esc(l.name) + '" ' +
              'data-mlsr-logoname="' + esc(l.id) + '" placeholder="Name it, e.g. Back print" aria-label="Name for this logo">';
        return '<div class="mlsr-logofile' + (l.previewUrl ? ' has-img' : '') + '">' + thumb +
          '<span class="mlsr-logofile__meta">' + label +
            '<em>' + esc(l.file.name) + ', ' + prettySize(l.file.size) + (l.previewUrl ? '' : ', no preview for this format') + '</em>' +
          '</span>' +
          '<button type="button" class="mlsr-logofile__x" data-act="rm-logo" data-id="' + esc(l.id) + '" aria-label="Remove ' + esc(l.name) + '">' + I.x + '</button>' +
        '</div>';
      }
      var mainLogo = state.logos.filter(function (l) { return l.slot === 'main'; })[0];
      var extras = LOGO_SLOTS.map(function (sl) {
        var mine = state.logos.filter(function (l) { return l.slot === sl.k; });
        var showAdd = sl.k === 'other' || !mine.length;
        return '<div class="mlsr-extra' + (mine.length ? ' has-file' : '') + '">' +
          '<p class="mlsr-extra__t">' + esc(sl.title) + '</p>' +
          '<p class="mlsr-extra__d">' + esc(sl.desc) + '</p>' +
          mine.map(logoRow).join('') +
          (showAdd
            ? '<label class="mlsr-drop mlsr-drop--sm">' +
                '<input type="file" data-mlsr-logofile="' + sl.k + '" accept="' + ACCEPT_ATTR + '"' + (sl.k === 'other' ? ' multiple' : '') + ' hidden>' +
                I.up + '<span><b>' + (mine.length ? 'Add another' : 'Upload') + '</b></span>' +
              '</label>'
            : '') +
        '</div>';
      }).join('');
      return twoCol(
        aside('Your brand', 'So we can mock your products up properly.', [
          // Marked so the answer from the logo finder, which arrives after this
          // has rendered, can correct it rather than leaving it contradicting
          // the offer sitting right next to it.
          '<span data-mlsr-asidelogo>' + (state.logoFound
            ? 'Use the logo we found on your site as your main logo, or upload a better one.'
            : 'We need your main logo to mock anything up.') + '</span>',
          'A vector file (AI, EPS or SVG) gives the cleanest print, but PNG or JPG is fine.',
          'Dark shirts and light shirts often need different artwork. Add the extra versions below and you will pick the right one for each product on the Design step.'
        ]),
        found +
        '<div class="mlsr-fields">' +
          '<div class="mlsr-field">' +
            '<p class="mlsr-label">Main logo' +
              (state.logoFound ? ' <span class="opt">(Skip this if you use the one above)</span>' : '<span class="req">*</span>') + '</p>' +
            '<p class="mlsr-logohint mlsr-logohint--top">The one we use by default, usually your full color logo.</p>' +
            '<div class="mlsr-logonote" role="note">' + I.info +
              '<span><b>Embroidering? No gradients.</b> Embroidered items are stitched in solid thread colors, so gradients, shadows and fades can not be reproduced. If your logo has one, upload a flat, solid color version under Single color version below. Gradients are fine on printed items.</span>' +
            '</div>' +
            (mainLogo
              ? '<div class="mlsr-logolist">' + logoRow(mainLogo) + '</div>'
              : '<label class="mlsr-drop' + (state.errors.logoFile ? ' is-invalid' : '') + '">' +
                  '<input type="file" data-mlsr-logofile="main" accept="' + ACCEPT_ATTR + '" hidden>' +
                  I.up + '<span>Drop your logo here or <b>browse</b></span>' +
                  '<em>Vector (AI, EPS, SVG) is best. PNG or JPG is fine. ' + MAX_LOGO_MB + ' MB max.</em>' +
                '</label>') +
            '<p class="mlsr-err" data-mlsr-logoerr="main" hidden></p>' + err('logoFile') +
          '</div>' +
          '<div class="mlsr-field mlsr-morelogos">' +
            '<p class="mlsr-label">More versions of your logo <span class="opt">(Optional)</span></p>' +
            '<p class="mlsr-logohint mlsr-logohint--top">One logo rarely works on everything. Add other versions here, then choose the right one for each product on the Design step.</p>' +
            '<div class="mlsr-extras">' + extras + '</div>' +
            '<p class="mlsr-logocount">' + state.logos.length + ' of ' + MAX_LOGOS + ' logos added, ' + MAX_LOGO_MB + ' MB each</p>' +
            '<p class="mlsr-err" data-mlsr-logoerr="more" hidden></p>' +
          '</div>' +
        '</div>' + foot('Continue')
      ) + helpBar();
    }
    // One definition, used by validation, by the step copy and by the summary,
    // so they can never disagree about whether artwork was supplied.
    function fileExt(name) {
      var m = String(name || '').match(/\.([a-z0-9]+)$/i);
      return m ? m[1] : '';
    }
    function prettySize(bytes) {
      if (!bytes) return '';
      return bytes < 1024 * 1024
        ? Math.max(1, Math.round(bytes / 1024)) + ' KB'
        : (bytes / 1024 / 1024).toFixed(1) + ' MB';
    }

    function allProductsBlank() {
      var ps = state.data.products;
      return ps.length > 0 && ps.every(function (p) { return p.blank; });
    }
    // The main logo is the one required upload. The logo pulled from their site
    // stands in for it if they accepted that instead.
    function haveArtwork() {
      return state.logos.some(function (l) { return l.slot === 'main'; }) || siteLogoOn();
    }
    function siteLogoOn() {
      return state.data.logoSource === 'site' && !!state.logoFound;
    }
    // "acme_left-chest.png" becomes "acme left chest", a decent starting name
    // that the customer can overwrite with where the logo actually goes.
    function baseName(n) {
      return String(n || '').replace(/\.[a-z0-9]+$/i, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Logo';
    }
    // Every logo the customer can pick from on the Design step: what they
    // uploaded, plus the one pulled from their site if they accepted it. The
    // main logo (or the site one standing in for it) always comes first, so it
    // is the default for every product.
    function logoOptions() {
      var out = state.logos.map(function (l) {
        var sl = slotInfo(l.slot);
        return {
          id: l.id, site: false, slot: l.slot, file: l.file, thumb: l.previewUrl || '',
          ext: (fileExt(l.file.name) || 'file').toUpperCase(),
          reason: sl ? sl.reason : '',
          name: l.slot === 'main' ? 'Main logo' : ((l.name || '').trim() || baseName(l.file.name))
        };
      });
      if (siteLogoOn()) {
        out.splice(out.length && out[0].slot === 'main' ? 1 : 0, 0, {
          id: 'site', site: true, slot: 'main', file: state.attachedLogo || null,
          thumb: state.logoFound.proxied, ext: 'LOGO', reason: '',
          name: 'Logo from ' + (siteHost() || 'your site')
        });
      }
      return out;
    }
    // A name with its reason, for the ticket: "Light version (for dark colored items)".
    function logoLabel(o) { return o.name + (o.reason ? ' (' + o.reason + ')' : ''); }
    // The logo a product uses: the one chosen, else the first. A stale choice
    // (the logo was removed, or this is a resumed draft with no files) quietly
    // falls back rather than pointing at nothing.
    function logoFor(p) {
      var opts = logoOptions();
      for (var k = 0; k < opts.length; k++) if (opts[k].id === (p && p.logoId)) return opts[k];
      return opts[0] || null;
    }

    function siteHost() {
      var h = (state.data.website || '').trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '').replace(/^www\./i, '');
      return (h && /\./.test(h)) ? h : '';
    }
    /* ---- step 3---------------------------------------------------------- */

    /* Pro and Enterprise carry twice the features of the smaller plans, which
       left the first two cards half empty and the row ragged. Every card is cut
       to the same depth, and the rest is one click away on the cards that have
       more. */
    function featCap() {
      var ts = cfg.tiers || [];
      var cap = 0;
      ts.slice(0, 2).forEach(function (t) { cap = Math.max(cap, (t.features || []).length); });
      return cap || 5;
    }

    function step3() {
      var cap = featCap();
      var cards = (cfg.tiers || []).map(function (t) {
        var list = t.features || [];
        var open = !!state.openFeats[t.id];
        var shown = open ? list : list.slice(0, cap);
        var hidden = list.length - shown.length;
        var feats = shown.map(function (f) {
          return '<li class="mlsr-tier__feat">' + I.check +
            '<span><span class="mlsr-tier__feat-label">' + esc(f.label) + '</span>' +
            (f.note ? '<span class="mlsr-tier__feat-note">' + esc(f.note) + '</span>' : '') + '</span></li>';
        }).join('');
        if (hidden > 0 || open) {
          // A span, not a button: the card itself is already a radio button and
          // nesting one inside another is invalid.
          feats += '<li class="mlsr-tier__more-li">' +
            '<span class="mlsr-tier__more" role="button" tabindex="0" data-act="more-feats" ' +
              'data-tier-id="' + esc(t.id) + '" aria-expanded="' + (open ? 'true' : 'false') + '">' +
              (open ? 'Show less' : 'View ' + hidden + ' more feature' + (hidden === 1 ? '' : 's')) +
              (open ? I.chevU : I.chevD) +
            '</span></li>';
        }
        var sel = state.data.storeTier === t.id;
        var quick = t.id === SELF_SERVE;
        // The route matters more than the price does, so it is stated at the
        // top of the card rather than in a footnote nobody reads.
        var mode = '<span class="mlsr-tier__mode mlsr-tier__mode--' + (quick ? 'now' : 'call') + '">' +
          (quick ? I.bolt + 'Quick start' : I.cal + 'Set up with you') + '</span>';
        return '<button type="button" role="radio" aria-checked="' + (sel ? 'true' : 'false') + '" ' +
          'class="mlsr-tier' + (t.popular ? ' mlsr-tier--pop' : '') + (quick ? ' mlsr-tier--now' : '') +
          '" data-tier="' + esc(t.id) + '">' +
            '<span class="mlsr-tier__head">' +
              '<span class="mlsr-tier__modes">' + mode +
                (t.popular ? '<span class="mlsr-tier__pop">Popular</span>' : '') + '</span>' +
              '<span class="mlsr-tier__top">' +
                '<span class="mlsr-tier__name">' + esc(t.id) + '</span></span>' +
              '<span class="mlsr-tier__tag">' + esc(t.tagline) + '</span>' +
              '<span class="mlsr-tier__price-row"><span class="mlsr-tier__price">' + esc(t.price) + '</span>' +
              (t.unit ? '<span class="mlsr-tier__unit">' + esc(t.unit) + '</span>' : '') + '</span>' +
            '</span><span class="mlsr-tier__bar"></span>' +
            '<ul class="mlsr-tier__feats">' + feats + '</ul>' +
          '</button>';
      }).join('');
      var selfName = SELF_SERVE;
      var others = (cfg.tiers || []).filter(function (t) { return t.id !== SELF_SERVE; })
        .map(function (t) { return t.id; });
      var othersLabel = others.length
        ? (others.length > 1
            ? others.slice(0, -1).join(', ') + ' and ' + others[others.length - 1]
            : others[0])
        : 'the larger plans';
      return '<div class="mlsr__head"><h2>Your plan</h2>' +
          '<p class="mlsr__sub">Pick the tier that fits your team. You can change it later.</p></div>' +
        '<div class="mlsr-routes">' +
          '<div class="mlsr-route mlsr-route--now">' + I.bolt +
            '<span><b>Quick start</b>' +
            '<em>' + esc(selfName) + ' is self serve. Keep going here and you will have picked your products and artwork in a few minutes.</em></span></div>' +
          '<div class="mlsr-route mlsr-route--call">' + I.cal +
            '<span><b>We set it up with you</b>' +
            '<em>' + esc(othersLabel) + ' involve things like department pages, single sign on and custom billing, so we take a few details and get on a 15 minute call.</em></span></div>' +
        '</div>' +
        '<div class="mlsr-tiers" role="radiogroup" aria-label="Store tier">' + cards + '</div>' +
        (state.errors.storeTier ? '<p class="mlsr-err" style="margin-top:14px">' + esc(state.errors.storeTier) + '</p>' : '') +
        foot('Continue', true) + helpBar();
    }

    /* ---- sales branch ----------------------------------------------------- */

    function stepSales() {
      var d = state.data;
      return twoCol(
        aside('Tell us more',
          '<b>' + esc(d.storeTier) + '</b> stores are built with you rather than self serve, because they involve things like ' +
          'department pages, single sign on and custom billing.',
          [
            'A couple of questions and we get the right person on it.',
            'You will hear from us within one business day.',
            'Nothing here is locked in, it just gives us a head start.'
          ]),
        '<div class="mlsr-fields">' +
          '<div class="mlsr-field">' +
            '<label class="mlsr-label" for="mlsr-head">How many people will actually order from the store?</label>' +
            '<select class="mlsr-select" id="mlsr-head" data-bind="salesHeadcount">' +
              ['', 'Under 100', '100 to 299', '300 to 999', '1,000 to 4,999', '5,000 or more'].map(function (o) {
                return '<option value="' + esc(o) + '"' + (d.salesHeadcount === o ? ' selected' : '') + '>' + (o || 'Select a range') + '</option>';
              }).join('') +
            '</select>' +
          '</div>' +
          '<div class="mlsr-field">' +
            '<label class="mlsr-label" for="mlsr-time">When would you like it live?</label>' +
            '<select class="mlsr-select" id="mlsr-time" data-bind="salesTimeline">' +
              ['', 'As soon as possible', 'Within a month', 'This quarter', 'Just exploring'].map(function (o) {
                return '<option value="' + esc(o) + '"' + (d.salesTimeline === o ? ' selected' : '') + '>' + (o || 'Select a timeframe') + '</option>';
              }).join('') +
            '</select>' +
          '</div>' +
          '<div class="mlsr-field">' +
            '<label class="mlsr-label" for="mlsr-snotes">What do you need the store to do?</label>' +
            '<textarea class="mlsr-input mlsr-textarea" id="mlsr-snotes" data-bind="salesNotes" rows="4" placeholder="e.g. separate pages per department, single sign on with Okta, invoice billing, new hire kits"></textarea>' +
          '</div>' +
        '</div>' +
        '<div class="mlsr-submit-err" data-mlsr-submiterr></div>' +
        '<div class="mlsr__foot">' +
          '<button type="button" class="mlsr-btn mlsr-btn--outline" data-act="back"' + (state.submitting ? ' disabled' : '') + '>' + I.chevL + ' Back</button>' +
          '<button type="button" class="mlsr-btn mlsr-btn--teal" data-act="submit"' + (state.submitting ? ' disabled' : '') + '>' +
            (state.submitting ? '<span class="mlsr-spin"></span> Sending' : 'Send my request ' + I.check) + '</button>' +
        '</div>'
      ) + helpBar();
    }

    /* ---- step 4 ----------------------------------------------------------- */

    function step4() {
      return '<div class="mlsr-p__head">' +
          '<h2>Add products to your store</h2>' +
          '<p class="mlsr__sub mlsr__sub--sm">Search for something specific, start from a pack, ' +
            'or filter the full catalog on the left.</p>' +
        '</div>' +
        '<div class="mlsr-cat" data-mlsr-cathost></div>' +
        (state.errors.products ? '<p class="mlsr-err" style="margin-top:12px">' + esc(state.errors.products) + '</p>' : '') +
        '<div class="mlsr-pickbar" data-mlsr-selbar></div>' +
        helpBar();
    }

    function mountPicker() {
      var hostEl = root.querySelector('[data-mlsr-cathost]');
      if (!hostEl) return;
      if (!window.MLSRCatalog) {
        hostEl.innerHTML = '<div class="mlsr-state"><h3>Catalog unavailable</h3><p>Please refresh the page.</p></div>';
        return;
      }
      window.MLSRCatalog.mount(hostEl, {
        baseUrl: '/collections/' + cfg.catalog_handle,
        packs: cfg.packs || [],
        popularTag: cfg.popular_tag || '',
        vendorFallback: cfg.vendor_fallback || 'Merchloop',
        isSelected: isSelected,
        toggle: toggleProduct,
        onSelectionChange: function (cb) { selectionListeners.push(cb); }
      });
      updateSelBar();
    }
    // The product step trades the ordinary footer for a bar pinned to the bottom
    // of the viewport, because choosing products is the one step where you scroll
    // a long way from the controls. It carries the running selection so you can
    // see what you have picked without scrolling back, which is the pattern Faire
    // and Deel both use for building an order out of a large catalog.
    function updateSelBar() {
      var bar = root.querySelector('[data-mlsr-selbar]');
      if (!bar) return;
      var ps = state.data.products;
      var n = ps.length;
      var MAX = 6;
      if (!n) { state.picksOpen = false; state.clearArmed = false; }
      var open = state.picksOpen;

      var thumbs = ps.slice(0, MAX).map(function (p) {
        return p.img
          ? '<img class="mlsr-pickbar__th" src="' + esc(p.img) + '" alt="" loading="lazy">'
          : '<span class="mlsr-pickbar__th mlsr-pickbar__th--none"></span>';
      }).join('');
      if (n > MAX) thumbs += '<span class="mlsr-pickbar__more">+' + (n - MAX) + '</span>';

      var label = n
        ? '<b>' + n + (n === 1 ? ' product' : ' products') + '</b> in your store' +
          (n >= MAX_PRODUCTS ? ' <em class="mlsr-pickbar__cap">at the ' + MAX_PRODUCTS + ' product limit</em>' : '')
        : 'Nothing picked yet';

      // With picks, the whole cluster is the handle for the list. Without any,
      // it is plain text, because there is nothing to open.
      var left = n
        ? '<button type="button" class="mlsr-pickbar__toggle" data-act="picks" ' +
            'aria-expanded="' + (open ? 'true' : 'false') + '" aria-controls="mlsr-picks">' +
            '<span class="mlsr-pickbar__ths">' + thumbs + '</span>' +
            '<span class="mlsr-pickbar__n">' + label + '</span>' +
            '<span class="mlsr-pickbar__caret">' + I.chevU + '</span>' +
          '</button>'
        : '<span class="mlsr-pickbar__n">' + label + '</span>';

      var rows = ps.map(function (p, i) {
        var sub = [p.brand && p.brand !== (cfg.vendor_fallback || 'Merchloop') ? p.brand : '', p.cat]
          .filter(Boolean).join(' · ');
        return '<li class="mlsr-picks__it">' +
          (p.img
            ? '<img class="mlsr-picks__th" src="' + esc(p.img) + '" alt="" loading="lazy">'
            : '<span class="mlsr-picks__th"></span>') +
          '<span class="mlsr-picks__t"><b>' + esc(p.title) + '</b>' +
            (sub ? '<em>' + esc(sub) + '</em>' : '') + '</span>' +
          '<button type="button" class="mlsr-picks__x" data-act="rm-product" data-i="' + i + '" ' +
            'aria-label="Remove ' + esc(p.title) + '">' + I.x + '</button>' +
        '</li>';
      }).join('');

      var panel = n
        ? '<div class="mlsr-picks" id="mlsr-picks"' + (open ? '' : ' hidden') + '>' +
            '<div class="mlsr-picks__head">' +
              '<b>In your store</b>' +
              '<span class="mlsr-picks__c">' + n + (n === 1 ? ' product' : ' products') + '</span>' +
              '<button type="button" class="mlsr-picks__clear' + (state.clearArmed ? ' is-armed' : '') + '" ' +
                'data-act="clear-picks">' + (state.clearArmed ? 'Tap again to clear' : 'Clear all') + '</button>' +
            '</div>' +
            '<ul class="mlsr-picks__list">' + rows + '</ul>' +
            '<p class="mlsr-picks__fine">You choose colorways and print locations for each of these in the next step.</p>' +
          '</div>'
        : '';

      bar.innerHTML = panel +
        (state.capNote
          ? '<p class="mlsr-capnote mlsr-capnote--bar">' + I.info + '<span>' + esc(state.capNote) + '</span></p>'
          : '') +
        '<div class="mlsr-pickbar__in">' +
          '<div class="mlsr-pickbar__l">' + left + '</div>' +
          '<div class="mlsr-pickbar__r">' +
            '<button type="button" class="mlsr-btn mlsr-btn--outline" data-act="back">' + I.chevL + ' Back</button>' +
            '<button type="button" class="mlsr-btn mlsr-btn--primary" data-act="next">Continue ' + I.chevR + '</button>' +
          '</div>' +
        '</div>';
      bar.classList.toggle('has-picks', n > 0);
      bar.classList.toggle('is-open', !!open);
    }

    /* ---- step 5 ----------------------------------------------------------- */

    /* Step 5 is a review, not a form.
       Every item already carries our standard setup, so the screen's job is to
       show what that setup is and make one thing easy: adding more colorways.
       Print detail is real but secondary, so it collapses behind a summary line
       rather than taking four controls of vertical space per item. */
    function step5() {
      var ps = state.data.products, multi = ps.length > 1;
      // "Printing" was wrong here too: this step covers embroidery, laser
      // engraving and sublimation as much as it covers print.
      return '<div class="mlsr__head"><h2>Colors and decoration</h2>' +
          '<p class="mlsr__sub">We have set each item up the standard way. Add any colorways you want on the store, and change the decoration only if you need to.</p></div>' +
        (ps.length ? '<div class="mlsr-dbar">' +
          '<p class="mlsr-dbar__t">' + I.check + ' Standard setup applied to ' +
            ps.length + (ps.length === 1 ? ' item' : ' items') + '</p>' +
          (multi ? '<button type="button" class="mlsr-btn mlsr-btn--sm mlsr-btn--outline" data-act="apply-all" data-i="0">' + I.copy + ' Copy first item\'s setup to all</button>' : '') +
        '</div>' : '<div class="mlsr-state"><h3>No products yet</h3><p>Go back a step and add some.</p></div>') +
        '<div class="mlsr-dlist">' + ps.map(function (p, i) { return detailCard(p, i, multi); }).join('') + '</div>' +
        foot('Continue') + helpBar();
    }

    // The row used to be labelled "Printing" for everything, which reads as a
    // claim about the method and is simply wrong on an embroidered hoody. Now
    // that the product tells us its technique, the label says it: "Embroidery
    // Right Chest, Horizontal, Full Color". Where no tag exists we fall back to
    // "Decoration", which is neutral rather than wrong.
    function decoLabel(p) {
      if (p && p.blank) return 'Not decorated';
      return (p && p.deco && p.decosrc === 'tag') ? p.deco : 'Decoration';
    }

    /* One line of plain English standing in for four controls. */
    function printSummary(p) {
      if (p && p.blank) return '';
      var parts = [p.logoPlacement].filter(Boolean);
      return parts.join(', ');
    }
    function styleLabel(v) {
      var hit = null;
      LOGO_STYLES.forEach(function (o) { if (o.v === v) hit = o.l; });
      return hit || v;
    }

    function detailCard(p, i, multi) {
      var pre = 'p' + i + '.';
      var invalid = Object.keys(state.errors).some(function (k) { return k.indexOf(pre) === 0; });
      var chosen = p.colorsRequested.split(/,\s*/).map(function (s) { return s.trim(); }).filter(Boolean);
      // More colorways on the store is the one thing this step should make easy,
      // so the swatches are the loudest thing on the card and there is a single
      // click that takes the lot.
      var all = (p.available || []).length &&
        chosen.length >= Math.min(p.available.length, MAX_COLORS);
      var colorCtl = (p.available && p.available.length)
        ? '<div class="mlsr-colorchips">' + p.available.map(function (c) {
            return '<button type="button" class="mlsr-colorchip" aria-pressed="' + (chosen.indexOf(c) !== -1 ? 'true' : 'false') +
              '" data-color="' + esc(c) + '" data-i="' + i + '">' + I.check + esc(c) + '</button>';
          }).join('') +
          (p.available.length > 1
            ? '<button type="button" class="mlsr-colorall" data-act="all-colors" data-i="' + i + '">' +
                (all ? 'Just the first' : 'Take ' + Math.min(p.available.length, MAX_COLORS)) + '</button>'
            : '') +
          '</div>' +
          (state.colorCap[i]
            ? '<p class="mlsr-capnote">' + I.info + '<span>' + MAX_COLORS + ' colors per product is the most we can set up on a store. ' +
              'Turn one off to swap it, and tell us the rest in the notes.</span></p>'
            : '')
        : '<input class="mlsr-input mlsr-input--sm" type="text" placeholder="e.g. Black, Navy, White" value="' +
          esc(p.colorsRequested) + '" data-pfield="colorsRequested" data-i="' + i + '">';

      var places = placementsFor(p);
      var placeCtl = '<div class="mlsr-places">' + places.map(function (pl) {
        return '<button type="button" class="mlsr-place" aria-pressed="' + (p.logoPlacement === pl ? 'true' : 'false') +
          '" data-place="' + esc(pl) + '" data-i="' + i + '">' + esc(pl) + '</button>';
      }).join('') + '</div>';

      // With two or more logos, which one goes on this product is the first
      // question, so it sits right under the colors as thumbnails to tap.
      var lopts = p.blank ? [] : logoOptions();
      var lcur = lopts.length > 1 ? logoFor(p) : null;
      var logoCtl = lcur
        ? '<div class="mlsr-logochoose">' +
            '<p class="mlsr-label">Which logo goes on this one?</p>' +
            '<div class="mlsr-logochips">' + lopts.map(function (o) {
              return '<button type="button" class="mlsr-logochip" aria-pressed="' + (o.id === lcur.id ? 'true' : 'false') +
                '" data-logo="' + esc(o.id) + '" data-i="' + i + '">' +
                (o.thumb
                  ? '<img src="' + esc(o.thumb) + '" alt="" onerror="this.style.visibility=\'hidden\'">'
                  : '<span class="mlsr-logochip__ext">' + esc(o.ext) + '</span>') +
                '<span class="mlsr-logochip__n">' + esc(o.name) + '</span></button>';
            }).join('') + '</div>' +
          '</div>'
        : '';

      // Opened either because they asked, or because something in there is wrong.
      // A blank product has no decoration to change, so its panel never opens.
      var open = p.blank ? false : (state.openPrint[i] || invalid);

      return '<div class="mlsr-dcard' + (invalid ? ' is-invalid' : '') + (open ? ' is-open' : '') + '">' +
        '<div class="mlsr-dcard__head">' +
          (p.img ? '<img class="mlsr-dcard__thumb" src="' + esc(p.img) + '" alt="" onerror="this.style.visibility=\'hidden\'">' : '<span class="mlsr-dcard__thumb"></span>') +
          '<span class="mlsr-dcard__name">' + esc(p.title) +
            (p.cat ? '<span class="mlsr-dcard__cat">' + esc(p.cat) + (p.deco ? ' · ' + esc(p.deco) : '') + '</span>' : '') + '</span>' +
          '<button type="button" class="mlsr-dcard__rm" data-act="rm-product" data-i="' + i + '" aria-label="Remove ' + esc(p.title) + '">' + I.x + '</button>' +
        '</div>' +
        '<div class="mlsr-dcard__body">' +
          '<div class="' + fcls(pre + 'colorsRequested') + ' mlsr-colorblock">' +
            '<p class="mlsr-label">Colors on the store' +
              '<span class="opt"> ' + chosen.length + ' of ' + Math.max(chosen.length, (p.available || []).length) + '</span></p>' +
            colorCtl + err(pre + 'colorsRequested') +
          '</div>' +
          logoCtl +
          '<div class="mlsr-printrow">' +
            '<p class="mlsr-printrow__s"><b>' + esc(decoLabel(p)) + '</b> ' + esc(printSummary(p)) + '</p>' +
            (p.blank ? '' : '<button type="button" class="mlsr-printrow__b" data-act="toggle-print" data-i="' + i + '" aria-expanded="' + (open ? 'true' : 'false') + '">' +
              (open ? 'Done' : 'Change') + (open ? I.chevU : I.chevR) + '</button>') +
          '</div>' +
          '<div class="mlsr-printpanel"' + (open ? '' : ' hidden') + '>' +
            '<div class="' + fcls(pre + 'logoPlacement') + '">' +
              '<p class="mlsr-label">Where does the logo go?</p>' +
              placeCtl + err(pre + 'logoPlacement') +
            '</div>' +
            '<div class="mlsr-printpanel__foot">' +
              (multi ? '<button type="button" class="mlsr-btn mlsr-btn--ghost" data-act="apply-all" data-i="' + i + '">' + I.copy + ' Use this setup for every item</button>' : '') +
            '</div>' +
          '</div>' +
        '</div></div>';
    }

    function sel(label, errKey, opts, value, placeholder, attr, i, j, field) {
      var options = '<option value="">' + esc(placeholder) + '</option>' + opts.map(function (o) {
        var v = typeof o === 'string' ? o : o.v;
        var l = typeof o === 'string' ? o : o.l;
        return '<option value="' + esc(v) + '"' + (v === value ? ' selected' : '') + '>' + esc(l) + '</option>';
      }).join('');
      return '<div class="' + fcls(errKey) + '">' +
        '<label class="mlsr-label">' + esc(label) + '</label>' +
        '<select class="mlsr-select mlsr-select--sm" data-' + attr + '="' + field + '" data-i="' + i + '"' +
        (j !== null && j !== undefined ? ' data-j="' + j + '"' : '') + '>' + options + '</select>' +
        err(errKey) + '</div>';
    }

    /* ---- step 6 ----------------------------------------------------------- */

    function step6() {
      var d = state.data;
      function row(k, v) { return '<div class="mlsr-dl__row"><dt>' + esc(k) + '</dt><dd>' + v + '</dd></div>'; }
      var left = [row('Company', esc(d.companyName)), row('Website', esc(d.website)),
        row('Name', esc((d.firstName + ' ' + d.lastName).trim())), row('Email', esc(d.email))];
      if (d.phone) left.push(row('Phone', esc(d.phone)));
      if (d.companySize) left.push(row('Company size', esc(d.companySize)));
      if (referralName()) left.push(row('Referred by', esc(referralName())));

      var right = [
        row('Tier', '<span class="mlsr-tierpill">' + esc(d.storeTier) + '</span>'),
        row('Products', d.products.length + ' item' + (d.products.length === 1 ? '' : 's')),
        (function () {
          var lo = logoOptions();
          return row(lo.length > 1 ? 'Logos' : 'Logo', lo.length === 1 && lo[0].site
            ? 'Using the one from ' + esc(siteHost())
            : (lo.length ? lo.map(function (o) { return esc(o.name); }).join(', ')
              : 'Not supplied'));
        })()
      ];
      var manyLogos = logoOptions().length > 1;

      var items = d.products.map(function (p) {
        return '<div class="mlsr-ritem">' +
          (p.img ? '<img src="' + esc(p.img) + '" alt="" onerror="this.style.visibility=\'hidden\'">' : '<span class="mlsr-ritem__ph"></span>') +
          '<div class="mlsr-ritem__c"><h4>' + esc(p.title) + '</h4>' +
          '<p>' + esc(p.colorsRequested) + '</p>' +
          '<p>' + esc(p.logoPlacement || '') + '</p>' +
          (manyLogos && !p.blank ? '<p>Logo: ' + esc(logoFor(p).name) + '</p>' : '') +
          '</div></div>';
      }).join('');

      return '<div class="mlsr__head"><h2>Review and send</h2>' +
          '<p class="mlsr__sub">One last look before it comes to us.</p></div>' +
        '<div class="mlsr-review">' +
          '<div class="mlsr-review__cols">' +
            '<div><h3>Your details</h3><dl class="mlsr-dl">' + left.join('') + '</dl></div>' +
            '<div><h3>Your store</h3><dl class="mlsr-dl">' + right.join('') + '</dl></div>' +
          '</div>' +
          '<div><h3>Products</h3><div class="mlsr-review__list">' + items + '</div></div>' +
        '</div>' +
        '<div class="mlsr-submit-err" data-mlsr-submiterr></div>' +
        '<div class="mlsr__foot">' +
          '<button type="button" class="mlsr-btn mlsr-btn--outline" data-act="back"' + (state.submitting ? ' disabled' : '') + '>' + I.chevL + ' Back</button>' +
          '<button type="button" class="mlsr-btn mlsr-btn--teal" data-act="submit"' + (state.submitting ? ' disabled' : '') + '>' +
            (state.submitting ? '<span class="mlsr-spin"></span> Sending' : 'Send my store request ' + I.check) + '</button>' +
        '</div>' + helpBar();
    }

    /* -- quick fill --------------------------------------------------------- */

    /* Kept as a reset, though it rarely has anything to do now that every item
       is set up the standard way the moment it is picked. */
    function expertChoice() {
      state.data.products.forEach(function (p) {
        p.logoPlacement = ''; p.logoStyle = '';
        applyDefaults(p);
      });
      state.errors = {}; exportToTray(); saveDraft(); render();
    }
    function applyToAll(i) {
      var src = state.data.products[i];
      if (!src) return;
      state.data.products.forEach(function (p, j) {
        if (j === i) return;
        var places = placementsFor(p);
        p.logoPlacement = places.indexOf(src.logoPlacement) !== -1 ? src.logoPlacement : places[0];
        p.logoStyle = src.logoStyle;
        var sl = logoFor(src);
        if (sl) p.logoId = sl.id;
      });
      state.errors = {}; exportToTray(); saveDraft(); render();
    }

    /* -- submission ---------------------------------------------------------- */

    function productsText() {
      var manyLogos = logoOptions().length > 1;
      return state.data.products.map(function (p, i) {
        var L = [];
        L.push((i + 1) + '. ' + p.title);
        if (p.blank) {
          L.push('   Category: ' + (p.cat || 'n/a') + (p.brand ? '  |  Brand: ' + p.brand : ''));
          L.push('   Colors: ' + p.colorsRequested);
          L.push('   BLANK - ships undecorated, no decoration location, no logo');
          L.push('   ' + location.origin + (p.url || '/products/' + p.h));
          return L.join('\n');
        }
        L.push('   Category: ' + (p.cat || 'n/a') + (p.brand ? '  |  Brand: ' + p.brand : '') +
          (p.deco ? '  |  Decoration: ' + p.deco +
            (p.decosrc === 'guess' ? ' (ASSUMED from category, no tag on the product, please confirm)' : '') : ''));
        L.push('   Colors: ' + p.colorsRequested);
        L.push('   Location 1: ' + (p.logoPlacement || ''));
        if (manyLogos) L.push('   Logo: ' + logoLabel(logoFor(p)));
        L.push('   ' + location.origin + (p.url || '/products/' + p.h));
        return L.join('\n');
      }).join('\n\n');
    }
    // Last resort when both send routes fail. Mail clients start truncating a
    // mailto somewhere past 2,000 characters, so this carries the details and
    // the product list as far as it safely can rather than producing a link
    // that silently arrives half empty.
    function mailtoEscape(to) {
      var d = state.data;
      var body = summaryText();
      if (body.length > 1500) {
        body = body.slice(0, 1500) + '\n\n[...truncated. Reply and we will collect the rest.]';
      }
      return 'mailto:' + to +
        '?subject=' + encodeURIComponent('Store request: ' + (d.companyName || 'new store')) +
        '&body=' + encodeURIComponent(body);
    }

    // Whether an uploaded logo really made it onto the ticket. Until the send
    // has tried, assume it will; afterwards the ticket tells the truth.
    function logoStatus(o) {
      return (state.noFiles || state.unattached[o.id])
        ? 'NOT attached (' + o.file.name + '), ask the customer to email it'
        : 'attached, ' + o.file.name;
    }

    function summaryText() {
      var d = state.data, o = [];
      o.push('STORE REQUEST: ' + d.companyName);
      o.push('Tier: ' + d.storeTier + (isSales() ? '  (needs our team, not self serve)' : ''));
      o.push('Website: ' + d.website);
      o.push('Company size: ' + (d.companySize || 'not given'));
      // In the body as well as in a property, because the body is the part that
      // definitely arrives. The property needs someone to create it in HubSpot
      // first; the ticket description reaches a human either way.
      if (referralName()) {
        o.push('Referred by: ' + referralName() +
          (state.data.referralRep === REFERRAL_OTHER || !state.data.referralRep
            ? '  (typed in, not picked from the list)' : ''));
      }
      var lo = logoOptions();
      function siteLines(pad) {
        var lm = state.logoFound.meta, L = [];
        L.push(pad + 'Pulled automatically from ' + siteHost() + ', not supplied by the customer.');
        L.push(pad + (lm.vector ? 'Vector file' : (lm.width + ' by ' + lm.height + ' ' + (lm.type || 'image')))
          + ', quality ' + (lm.quality || 'unknown') + ', found via ' + (lm.source || 'unknown'));
        L.push(pad + 'Source: ' + d.logoFoundUrl);
        if (!lm.vector) L.push(pad + 'Request production artwork before proofing.');
        return L;
      }
      if (lo.length === 1 && !lo[0].site) {
        o.push('Logo: ' + logoStatus(lo[0]));
      } else if (lo.length === 1) {
        o.push('Logo: NOT supplied by the customer. ' + siteLines('  ')[0].trim());
        siteLines('  ').slice(1).forEach(function (t) { o.push(t); });
      } else if (lo.length > 1) {
        // Several logos: number them, and each product below says which one it uses.
        o.push('LOGOS (' + lo.length + '), each product below says which one to use');
        lo.forEach(function (x, k) {
          if (x.site) {
            o.push('  ' + (k + 1) + '. ' + logoLabel(x) + ': NOT supplied by the customer');
            siteLines('     ').forEach(function (t) { o.push(t); });
          } else {
            o.push('  ' + (k + 1) + '. ' + logoLabel(x) + ': ' + logoStatus(x));
          }
        });
      } else {
        o.push('Logo: not supplied. Ask the customer for artwork.');
      }
      if (isSales()) {
        o.push('');
        o.push('QUALIFICATION');
        o.push('Store audience: ' + (d.salesHeadcount || 'not given'));
        o.push('Timeline: ' + (d.salesTimeline || 'not given'));
        o.push('Needs: ' + (d.salesNotes || 'not given'));
      } else {
        o.push('');
        o.push('PRODUCTS (' + d.products.length + ')');
        o.push(productsText());
      }
      return o.join('\n');
    }

    // Same mechanism as snippets/mlb-request-tray.liquid: HubSpot's embedded
    // form, filled and submitted off screen. It is the only path that carries a
    // file, and it means the tray and the wizard feed one form.
    function submitHubSpot() {
      if (!cfg.hubspot_portal_id || !cfg.hubspot_form_guid) return Promise.reject(new Error('hubspot not configured'));
      var d = state.data;
      // Mapped to properties that actually exist in the portal. The three
      // merchloop_* fields this used to send were never real, so they silently
      // dropped. subject and content are ticket properties: the form is a
      // support form wired to Help Desk, so a submission becomes a ticket with
      // this subject line and the whole request in its description.
      var values = {
        email: d.email.trim(),
        firstname: d.firstName.trim(),
        lastname: d.lastName.trim(),
        phone: d.phone.trim(),
        company: d.companyName.trim(),
        website: d.website.trim(),
        lead_source_detail: 'Store request wizard',
        // The form does not carry this field yet. setField() returns false for
        // a field the form lacks, so sending it costs nothing today and starts
        // populating the moment a "Company size" field mapped to the
        // `company_size` contact property is added to the form in HubSpot.
        company_size: d.companySize,
        // Same arrangement as company_size. Nothing in the portal carries this
        // yet, so it is dropped harmlessly until a "Referred by" field mapped
        // to a `referred_by_rep` contact property is added to the form. Keep it
        // a plain string, not an enumeration: an enumeration silently discards
        // any value outside its option list, which is exactly how we would lose
        // the attribution the day a new rep joins.
        referred_by_rep: referralName(),
        message: summaryText()
      };
      // Ticket properties on a support form are namespaced, so the inputs are
      // named TICKET.subject and TICKET.content rather than subject and
      // content. Both are required, so getting this wrong fails validation
      // silently: onFormSubmitted never fires and the wizard times out. Send
      // both spellings, since the fill loop skips any field the form lacks.
      var subject = 'Store request: ' + d.companyName.trim() + ' (' + d.storeTier + ')';
      var body = summaryText();
      values['TICKET.subject'] = subject;
      values['TICKET.content'] = body;
      values.subject = subject;
      values.content = body;
      // The form now carries a "Referred by" ticket property (internal name
      // referred_by), so the typed or picked rep name lands on the ticket as its
      // own field. Same two spellings as subject and content.
      values['TICKET.referred_by'] = referralName();
      values.referred_by = referralName();
      return loadHubSpot().then(function () {
        return new Promise(function (resolve, reject) {
          var hostId = 'mlsr-hs-' + Date.now();
          var holder = document.createElement('div');
          holder.id = hostId;
          holder.style.cssText = 'position:absolute;left:-9999px;top:0;width:1px;height:1px;overflow:hidden';
          document.body.appendChild(holder);
          var done = false;
          // 20s was not enough headroom once a logo file rides along on a slow
          // connection, and a timeout here dumps the customer into the Shopify
          // fallback for no good reason.
          var waitMs = parseInt(cfg.hubspot_timeout_ms, 10) || 45000;
          var timer = setTimeout(function () {
            if (!done) {
              done = true; cleanup();
              reject(new Error('HubSpot timed out after ' + Math.round(waitMs / 1000) + 's'));
            }
          }, waitMs);
          function cleanup() { clearTimeout(timer); setTimeout(function () { holder.remove(); }, 200); }

          window.hbspt.forms.create({
            portalId: cfg.hubspot_portal_id,
            formId: cfg.hubspot_form_guid,
            target: '#' + hostId,
            onFormReady: function (el) {
              var form = el && el.nodeName === 'FORM' ? el : (el && el.querySelector ? el.querySelector('form') : null);
              var scope = form || el || holder;

              // A select only accepts a value that is one of its options, and
              // assigning anything else silently leaves it empty, which then
              // fails validation with no visible reason.
              function setField(name, value) {
                var input = scope.querySelector('[name="' + name + '"]');
                if (!input) return false;
                if (input.tagName === 'SELECT') {
                  var want = String(value), hit = false;
                  for (var i = 0; i < input.options.length; i++) {
                    if (input.options[i].value === want) { hit = true; break; }
                  }
                  if (!hit) return false;
                }
                input.value = value;
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('change', { bubbles: true }));
                return true;
              }

              function fill() {
                Object.keys(values).forEach(function (name) { setField(name, values[name]); });
                // The property carries both the exact tier names and two
                // broader buckets, so prefer the precise one and fall back.
                if (!setField('store_type_requested', state.data.storeTier)) {
                  setField('store_type_requested',
                    isSales() ? 'SAAS Paid Subscription' : 'Quick Start (Free Essential Store)');
                }
              }
              fill();

              // Rebuilds the ticket text once we know which files really went,
              // so it never claims an attachment that is not there.
              function refreshBody() {
                body = summaryText();
                values['TICKET.content'] = body;
                values.content = body;
                values.message = body;
                fill();
              }
              state.unattached = {};
              var upl = logoOptions().filter(function (o) { return !!o.file; });
              if (upl.length) {
                var fi = scope.querySelector('input[type="file"]');
                var okIds = {};
                if (fi && window.DataTransfer) {
                  try {
                    var dt = new DataTransfer();
                    // A HubSpot file field only keeps several files when
                    // "Allow multiple files" is switched on for it.
                    var take = fi.multiple ? upl : upl.slice(0, 1);
                    take.forEach(function (o) { dt.items.add(o.file); okIds[o.id] = true; });
                    fi.files = dt.files;
                    fi.dispatchEvent(new Event('change', { bubbles: true }));
                  } catch (e) { okIds = {}; }
                }
                upl.forEach(function (o) { if (!okIds[o.id]) state.unattached[o.id] = true; });
                if (Object.keys(state.unattached).length) refreshBody();
              }
              // HubSpot re-renders the form shortly after onFormReady, which
              // wipes anything set too early, and the re-render takes longer
              // the more text we put in. A fixed wait is a race that a big
              // basket loses, so instead: fill, confirm the value actually
              // stuck, and only then click. The button is looked up at click
              // time, because the one captured now may be replaced by the
              // re-render and clicking a detached node does nothing at all.
              var tries = 0;
              (function attempt() {
                tries++;
                fill();
                setTimeout(function () {
                  if (done) return;
                  var ta = scope.querySelector('[name="TICKET.content"]');
                  var stuck = ta && String(ta.value || '').length === body.length;
                  if (!stuck && tries < 8) return attempt();
                  var btn = scope.querySelector('input[type="submit"], button[type="submit"]');
                  if (!btn) {
                    if (tries < 8) return attempt();
                    done = true; cleanup();
                    return reject(new Error('HubSpot submit button never rendered'));
                  }
                  if (!stuck && window.console) {
                    console.warn('[mlsr] ticket body did not settle after ' + tries + ' tries, sending anyway');
                  }
                  btn.click();
                  // One re-click if nothing happened, which covers the case
                  // where the first click landed mid re-render.
                  setTimeout(function () {
                    if (done) return;
                    var again = scope.querySelector('input[type="submit"], button[type="submit"]');
                    if (again) { fill(); again.click(); }
                  }, 2500);
                }, 260);
              })();
            },
            onFormSubmitted: function () {
              if (done) return;
              done = true; cleanup(); resolve({ ok: true, via: 'hubspot' });
            }
          });
        });
      });
    }
    function loadHubSpot() {
      if (window.hbspt && window.hbspt.forms) return Promise.resolve();
      return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = 'https://js.hsforms.net/forms/embed/v2.js';
        s.async = true;
        s.onload = function () { (window.hbspt && window.hbspt.forms) ? resolve() : reject(new Error('hbspt missing')); };
        s.onerror = function () { reject(new Error('hsforms script blocked')); };
        document.head.appendChild(s);
      });
    }

    // Shopify's bot protection rejects any POST to /contact that does not carry
    // its challenge, so fetching that endpoint returns 400 every time, even for
    // a minimal valid body. The challenge is injected into genuinely rendered
    // {% form 'contact' %} markup, so the fallback fills in the real form in
    // the section and submits it for real. That navigates the page, so this
    // promise deliberately never settles: the success screen is picked up on
    // the way back, in resumeAfterFallback below.
    var POSTED_KEY = 'mlsrPosted:v1';

    function submitShopify() {
      var d = state.data;
      var form = document.getElementById('mlsr-contact-fallback');
      if (!form) return Promise.reject(new Error('no fallback form'));

      // This route is a plain text form and cannot carry files at all, so the
      // ticket has to say the logos did not come with it.
      state.noFiles = true;
      var vals = {
        name: (d.firstName + ' ' + d.lastName).trim(),
        email: d.email.trim(),
        phone: d.phone.trim(),
        company: d.companyName.trim(),
        website: d.website.trim(),
        tier: d.storeTier,
        reqtype: isSales() ? 'Store request, needs our team' : 'Store request, self serve',
        logo: logoOptions().length
          ? logoOptions().map(function (o) { return o.file ? o.file.name : o.name; }).join(', ')
          : 'Not supplied',
        body: summaryText()
      };
      var missing = [];
      Object.keys(vals).forEach(function (k) {
        var el = form.querySelector('[data-mlsr-ff="' + k + '"]');
        if (!el) { missing.push(k); return; }
        el.value = vals[k];
      });
      if (missing.length) return Promise.reject(new Error('fallback fields missing: ' + missing.join(',')));

      try {
        sessionStorage.setItem(POSTED_KEY, JSON.stringify({
          route: isSales() ? 'sales' : 'self',
          t: Date.now()
        }));
      } catch (e) {}

      // A real click, not form.submit(), so Shopify's own submit handling and
      // its challenge script both run the way they do for any other form.
      var btn = form.querySelector('[data-mlsr-ff-submit]');
      if (btn) btn.click(); else form.submit();

      // Normally the page navigates and nothing after this runs. If it does
      // not, something blocked the submit and the customer must not be left
      // watching a spinner forever.
      return new Promise(function (resolve, reject) {
        setTimeout(function () {
          try { sessionStorage.removeItem(POSTED_KEY); } catch (e) {}
          reject(new Error('the contact form did not submit'));
        }, 15000);
      });
    }

    // Coming back from that navigation. Shopify appends contact_posted=true on
    // success, and the marker proves the post was ours rather than some other
    // form on the page.
    function resumeAfterFallback() {
      if (!/[?&]contact_posted=true/.test(window.location.search)) return false;
      var raw = null;
      try { raw = sessionStorage.getItem(POSTED_KEY); } catch (e) {}
      if (!raw) return false;
      var mark;
      try { mark = JSON.parse(raw); } catch (e) { return false; }
      try { sessionStorage.removeItem(POSTED_KEY); } catch (e) {}
      // Stale marker from an older tab should not hijack a fresh visit.
      if (!mark || !mark.t || Date.now() - mark.t > 10 * 60 * 1000) return false;

      clearDraft();
      trayWrite([]);
      root.setAttribute('data-done', mark.route === 'sales' ? 'sales' : 'self');
      root.classList.add('is-done');
      return true;
    }

    function submit() {
      if (state.submitting) return;
      // Never send a request with no artwork, even if something got past step 2.
      if (!haveArtwork() && !allProductsBlank()) { goTo(2); return; }
      if (!isSales() && !validateStep(5)) { goTo(5); focusFirstError(); return; }
      state.submitting = true;
      state.noFiles = false;
      state.unattached = {};
      render();
      // If they chose the logo found on their site, turn it into a real file
      // first so it actually rides along, rather than the ticket just naming
      // a URL. A failure here is not fatal; the request still goes.
      fetchFoundLogo()
        .then(function (file) { if (file) state.attachedLogo = file; })
        .then(submitHubSpot)
        .catch(function (e) {
          // Why HubSpot failed is the single most useful fact when a request
          // goes missing, and it used to be discarded into a console warning
          // nobody was watching. Carry it, so if the fallback fails too the
          // message names the first cause rather than only the second.
          state.hsError = (e && e.message) ? e.message : 'unknown';
          if (window.console) console.warn('[mlsr] HubSpot send failed, falling back:', state.hsError, e);
          return submitShopify();
        })
        .then(function () {
          state.submitting = false;
          clearDraft();
          trayWrite([]);
          root.setAttribute('data-done', isSales() ? 'sales' : 'self');
          root.classList.add('is-done');
          try {
            if (window.dataLayer) window.dataLayer.push({
              event: 'store_request_submitted', tier: state.data.storeTier,
              products: state.data.products.length, route: isSales() ? 'sales' : 'self serve'
            });
            if (window.fbq) window.fbq('track', 'Lead');
          } catch (e) {}
          scrollTop();
        })
        .catch(function (e) {
          state.submitting = false;
          render();
          var box = root.querySelector('[data-mlsr-submiterr]');
          if (box) {
            // Both routes failed. Say so plainly, name both causes for us, and
            // give them a way out that does not depend on either one working.
            var why = [];
            if (state.hsError) why.push('HubSpot: ' + state.hsError);
            if (e && e.message) why.push('fallback: ' + e.message);
            var to = cfg.fallback_email || 'hello@merchloop.com';
            box.innerHTML = 'We could not send your request automatically. ' +
              'Nothing you entered is lost, so you can try again. ' +
              'If it keeps failing, <a href="' + mailtoEscape(to) + '">email it to us instead</a> ' +
              'and we will pick it up from there.' +
              (why.length ? '<em class="mlsr-err__why">' + esc(why.join('  |  ')) + '</em>' : '');
            box.classList.add('is-shown');
          }
          if (window.console) console.error('[mlsr] submit failed', { hubspot: state.hsError, fallback: e });
        });
    }

    /* -- events -------------------------------------------------------------- */

    // Capture phase on purpose. The bubble handler below re-renders the bar, so
    // by the time a bubble listener ran, ev.target would already be detached and
    // the containment test would wrongly read as a click outside.
    document.addEventListener('click', function (ev) {
      if (!state.picksOpen) return;
      var bar = root.querySelector('[data-mlsr-selbar]');
      if (bar && bar.contains(ev.target)) return;
      state.picksOpen = false;
      state.clearArmed = false;
      updateSelBar();
    }, true);

    document.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Escape' || !state.picksOpen) return;
      // The product popup sits on top, so Escape belongs to it first.
      if (document.querySelector('[data-mlsr-qvmodal].is-open')) return;
      state.picksOpen = false;
      state.clearArmed = false;
      updateSelBar();
    });

    root.addEventListener('click', function (ev) {
      var a = ev.target.closest('[data-act]');
      var tier = ev.target.closest('[data-tier]');
      var chip = ev.target.closest('[data-color]');
      var place = ev.target.closest('[data-place]');
      var logoChip = ev.target.closest('[data-logo]');

      // Anything pointing at the meetings link opens over the wizard instead of
      // navigating away, wherever it came from: the help bar this file renders,
      // or the success screens Liquid renders. Matching on the destination means
      // there is no attribute to keep in step across two files.
      var bookA = cfg.help_meeting_url && ev.target.closest('a[href], [data-act="book"]');
      if (bookA && (bookA.getAttribute('data-act') === 'book' ||
                    bookA.getAttribute('href') === cfg.help_meeting_url)) {
        // A modified click still does what the browser would normally do.
        if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button > 0) return;
        if (openBooking()) ev.preventDefault();
        return;
      }

      if (a) {
        var act = a.getAttribute('data-act');
        if (act === 'start') { state.started = true; saveDraft(); goTo(1); return; }
        if (act === 'next') return next();
        if (act === 'back') return back();
        if (act === 'submit') return submit();
        if (act === 'expert') return expertChoice();
        if (act === 'apply-all') return applyToAll(parseInt(a.getAttribute('data-i'), 10));
        if (act === 'chat') return openChat();
        if (act === 'book') return;   // handled above, before this block
        if (act === 'use-found') {
          state.data.logoSource = state.data.logoSource === 'site' ? '' : 'site';
          saveDraft(); render(); return;
        }
        if (act === 'rm-logo') {
          var rid = a.getAttribute('data-id');
          state.logos = state.logos.filter(function (l) {
            if (l.id !== rid) return true;
            releasePreview(l);
            return false;
          });
          if (!state.logos.length && state.data.logoSource === 'upload') state.data.logoSource = '';
          saveDraft(); render();
          return;
        }
        if (act === 'more-feats') {
          var tid = a.getAttribute('data-tier-id');
          state.openFeats[tid] = !state.openFeats[tid];
          render(); return;
        }
        if (act === 'toggle-print') {
          var ti = parseInt(a.getAttribute('data-i'), 10);
          state.openPrint[ti] = !state.openPrint[ti];
          render(); return;
        }
        if (act === 'all-colors') {
          var ci = parseInt(a.getAttribute('data-i'), 10);
          var cp = state.data.products[ci], av = cp.available || [];
          var cap = Math.min(av.length, MAX_COLORS);
          var have = cp.colorsRequested.split(/,\s*/).filter(Boolean).length;
          // "Take all" can only ever take as many as we allow, so the button
          // and the chips can never disagree about what the limit is.
          cp.colorsRequested = have >= cap ? (av[0] || '') : av.slice(0, cap).join(', ');
          delete state.errors['p' + ci + '.colorsRequested'];
          exportToTray(); saveDraft(); render(); return;
        }
        if (act === 'picks') {
          state.picksOpen = !state.picksOpen;
          state.clearArmed = false;
          updateSelBar();
          return;
        }
        if (act === 'clear-picks') {
          // Two taps rather than a dialog. Emptying a list someone spent ten
          // minutes building deserves a beat, but not a modal.
          if (!state.clearArmed) { state.clearArmed = true; updateSelBar(); return; }
          state.data.products = [];
          state.clearArmed = false;
          exportToTray(); saveDraft(); notifySelection(); updateSelBar();
          return;
        }
        if (act === 'rm-product') {
          var rmI = parseInt(a.getAttribute('data-i'), 10);
          state.data.products.splice(rmI, 1);
          exportToTray(); saveDraft(); notifySelection();
          // Removing from the picked list must not re-render the step: that
          // would rebuild the catalog host and throw away the browse you are
          // in the middle of. notifySelection has already repainted the cards.
          if (a.closest('[data-mlsr-selbar]')) {
            state.clearArmed = false;
            updateSelBar();
            return;
          }
          render(); return;
        }
        if (act === 'draft-resume') return applyDraft();
        if (act === 'draft-discard') { clearDraft(); elDraft.classList.remove('is-shown'); return; }
      }
      if (tier) {
        state.data.storeTier = tier.getAttribute('data-tier');
        delete state.errors.storeTier;
        root.setAttribute('data-mode', isSales() ? 'sales' : 'self');
        saveDraft(); render(); return;
      }
      if (chip) {
        var ci = parseInt(chip.getAttribute('data-i'), 10);
        var cv = chip.getAttribute('data-color');
        var item = state.data.products[ci];
        var cur = item.colorsRequested.split(/,\s*/).map(function (s) { return s.trim(); }).filter(Boolean);
        var at = cur.indexOf(cv);
        if (at > -1) {
          cur.splice(at, 1);
          state.colorCap[ci] = false;
        } else if (cur.length >= MAX_COLORS) {
          // Turning one off to make room still works, so this is a nudge
          // rather than a wall. The note sits on the card it belongs to.
          state.colorCap[ci] = true;
          render(); return;
        } else {
          cur.push(cv);
          state.colorCap[ci] = false;
        }
        item.colorsRequested = cur.join(', ');
        delete state.errors['p' + ci + '.colorsRequested'];
        exportToTray(); saveDraft(); render(); return;
      }
      if (logoChip) {
        var lpi = parseInt(logoChip.getAttribute('data-i'), 10);
        if (state.data.products[lpi]) {
          state.data.products[lpi].logoId = logoChip.getAttribute('data-logo');
          saveDraft(); render();
        }
        return;
      }
      if (place) {
        var pi = parseInt(place.getAttribute('data-i'), 10);
        var pv = place.getAttribute('data-place');
        var prod = state.data.products[pi];
        prod.logoPlacement = pv;
        delete state.errors['p' + pi + '.logoPlacement'];
        exportToTray(); saveDraft(); render();
      }
    });

    function openChat() {
      try {
        if (window.HubSpotConversations && window.HubSpotConversations.widget) {
          window.HubSpotConversations.widget.open();
          return;
        }
      } catch (e) {}
      window.location.href = 'mailto:' + (cfg.fallback_email || 'hello@merchloop.com') +
        '?subject=' + encodeURIComponent('Question about starting a store');
    }

    /* -- the booking modal ---------------------------------------------------
       Sending someone to a new tab in the middle of a form is how you lose them,
       so the meetings page opens over the wizard and closes back onto the step
       they were on. Their draft is untouched throughout.
       Appended to <body> rather than inside .mlsr, because every ancestor there
       clips or scrolls. */

    var booking = null;
    function bookingUrl() {
      var u = cfg.help_meeting_url || '';
      if (!u) return '';
      // HubSpot's meetings pages render a compact, frameable layout with this.
      if (u.indexOf('embed=') === -1) u += (u.indexOf('?') === -1 ? '?' : '&') + 'embed=true';
      var d = state.data;
      // Anything they have already typed is theirs, so prefill rather than ask
      // for it a second time.
      if (d.email) u += '&email=' + encodeURIComponent(d.email);
      if (d.firstName) u += '&firstName=' + encodeURIComponent(d.firstName);
      if (d.lastName) u += '&lastName=' + encodeURIComponent(d.lastName);
      if (d.companyName) u += '&company=' + encodeURIComponent(d.companyName);
      return u;
    }
    function openBooking() {
      var url = bookingUrl();
      if (!url) return false;
      closeBooking();
      booking = document.createElement('div');
      booking.className = 'mlsr-modal';
      booking.setAttribute('role', 'dialog');
      booking.setAttribute('aria-modal', 'true');
      booking.setAttribute('aria-label', 'Book a 15 minute call');
      booking.setAttribute('data-mlsr-booking', '');
      booking.innerHTML =
        '<div class="mlsr-modal__back" data-act="close-book"></div>' +
        '<div class="mlsr-modal__box">' +
          '<div class="mlsr-modal__head">' +
            '<p class="mlsr-modal__t">Book a 15 minute call</p>' +
            '<button type="button" class="mlsr-modal__x" data-act="close-book" aria-label="Close">' + I.x + '</button>' +
          '</div>' +
          '<iframe class="mlsr-modal__frame" title="Book a 15 minute call" src="' + esc(url) + '" ' +
            'referrerpolicy="no-referrer-when-downgrade"></iframe>' +
          '<p class="mlsr-modal__foot">Trouble loading? ' +
            '<a href="' + esc(cfg.help_meeting_url) + '" target="_blank" rel="noopener">Open the booking page in a new tab</a>.</p>' +
        '</div>';
      document.body.appendChild(booking);
      document.documentElement.style.overflow = 'hidden';
      var x = booking.querySelector('.mlsr-modal__x');
      if (x) x.focus();
      booking.addEventListener('click', function (ev) {
        if (ev.target.closest('[data-act="close-book"]')) { ev.preventDefault(); closeBooking(); }
      });
      return true;
    }
    function closeBooking() {
      if (!booking) return;
      booking.remove();
      booking = null;
      document.documentElement.style.overflow = '';
    }
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && booking) closeBooking();
    });

    root.addEventListener('input', function (ev) {
      var t = ev.target;
      if (t.hasAttribute('data-mlsr-logoname')) {
        var nid = t.getAttribute('data-mlsr-logoname');
        state.logos.forEach(function (l) { if (l.id === nid) l.name = t.value; });
        return;
      }
      if (t.hasAttribute('data-bind')) {
        var key = t.getAttribute('data-bind');
        state.data[key] = t.value;
        if (state.errors[key]) {
          delete state.errors[key];
          var f = t.closest('.mlsr-field');
          if (f) { f.classList.remove('is-invalid'); var e2 = f.querySelector('.mlsr-err'); if (e2) e2.remove(); }
        }
        saveDraft();
        return;
      }
      if (t.hasAttribute('data-pfield')) {
        state.data.products[parseInt(t.getAttribute('data-i'), 10)][t.getAttribute('data-pfield')] = t.value;
        exportToTray(); saveDraft();
      }
    });

    root.addEventListener('change', function (ev) {
      var t = ev.target;
      if (t.hasAttribute('data-mlsr-logofile')) return takeLogos(t.files, t.getAttribute('data-mlsr-logofile'));
      // Revealed by hand rather than by re-rendering the step. render() rebuilds
      // every field on the page, which to show one text box would mean the
      // customer watching their own cursor jump out of whatever they were
      // typing. Toggling one node costs nothing and keeps the page still.
      if (t.hasAttribute('data-mlsr-refsel')) {
        state.data.referralRep = t.value;
        var rwrap = root.querySelector('[data-mlsr-refother]');
        if (rwrap) {
          var wantsTyped = t.value === REFERRAL_OTHER;
          rwrap.hidden = !wantsTyped;
          var rinp = rwrap.querySelector('input');
          if (wantsTyped) { if (rinp) rinp.focus(); }
          else { state.data.referralOther = ''; if (rinp) rinp.value = ''; }
        }
        saveDraft();
        return;
      }
      if (t.hasAttribute('data-pfield')) {
        var i = parseInt(t.getAttribute('data-i'), 10);
        var field = t.getAttribute('data-pfield');
        state.data.products[i][field] = t.value;
        clearFieldError(t, 'p' + i + '.' + field);
        exportToTray(); saveDraft();
        return;
      }
    });

    function takeLogos(list, slot) {
      slot = slot || 'other';
      var files = Array.prototype.slice.call(list || []);
      if (!files.length) return;
      // Everything except "something else" holds exactly one file.
      if (slot !== 'other') files = files.slice(0, 1);
      var msgs = [], added = 0, overCap = 0, info = slotInfo(slot);
      files.forEach(function (file) {
        // Replacing a slot's file frees its place, so only count the others.
        var held = state.logos.filter(function (l) { return slot === 'other' || l.slot !== slot; }).length;
        if (held >= MAX_LOGOS) { overCap++; return; }
        if (file.size > MAX_LOGO_MB * 1024 * 1024) {
          msgs.push(file.name + ' is over ' + MAX_LOGO_MB + ' MB. Email it to us instead and we\'ll attach it.');
          return;
        }
        var okType = ACCEPT.indexOf(file.type) !== -1 || /\.(ai|eps|svg|pdf|png|jpe?g|gif|webp)$/i.test(file.name);
        if (!okType) { msgs.push(file.name + ' is not a supported type. Try PNG, JPG, SVG, PDF, AI or EPS.'); return; }
        // The same file picked twice is almost always a slip, not two logos.
        var dupe = state.logos.some(function (l) { return l.file.name === file.name && l.file.size === file.size; });
        if (dupe) return;
        if (slot !== 'other') {
          state.logos = state.logos.filter(function (l) {
            if (l.slot !== slot) return true;
            releasePreview(l);
            return false;
          });
        }
        // Names have to be distinct or the picker on the Design step would
        // show two identical chips.
        var nm = slot === 'main' ? 'Main logo' : (info && info.name) || baseName(file.name), base = nm, n = 2;
        while (state.logos.some(function (l) { return l.name.toLowerCase() === nm.toLowerCase(); })) nm = base + ' ' + (n++);
        state.logos.push({ id: 'u' + (++logoUid), slot: slot, file: file, name: nm, previewUrl: previewFor(file) });
        added++;
      });
      if (overCap) msgs.push('You can add up to ' + MAX_LOGOS + ' logos. Email the rest to us and we\'ll attach them.');
      if (added) {
        // Main logo first, then the others in the order they are listed.
        state.logos = state.logos
          .map(function (l, i) { return { l: l, i: i }; })
          .sort(function (x, y) {
            return (SLOT_ORDER.indexOf(x.l.slot) - SLOT_ORDER.indexOf(y.l.slot)) || (x.i - y.i);
          })
          .map(function (w) { return w.l; });
        // Keeps "use the one from my site" if they chose it: it is just
        // another logo to pick from now.
        if (state.data.logoSource !== 'site') state.data.logoSource = 'upload';
        delete state.errors.logoFile;
        saveDraft();
      }
      render();
      var box = root.querySelector('[data-mlsr-logoerr="' + (slot === 'main' ? 'main' : 'more') + '"]');
      if (box && msgs.length) { box.textContent = msgs.join(' '); box.hidden = false; }
    }

    // An object URL holds the file in memory until it is revoked, so removing a
    // logo releases its preview rather than leaking it.
    function previewFor(file) {
      if (!file || !window.URL || !URL.createObjectURL) return '';
      var ext = fileExt(file.name).toLowerCase();
      var drawable = /^image\//.test(file.type || '') ||
        ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].indexOf(ext) !== -1;
      if (!drawable) return '';
      try { return URL.createObjectURL(file); } catch (e) { return ''; }
    }
    function releasePreview(l) {
      if (l && l.previewUrl) { try { URL.revokeObjectURL(l.previewUrl); } catch (e) {} l.previewUrl = ''; }
    }

    function clearFieldError(input, key) {
      if (!state.errors[key]) return;
      delete state.errors[key];
      var f = input.closest('.mlsr-field');
      if (f) { f.classList.remove('is-invalid'); var e = f.querySelector('.mlsr-err'); if (e) e.remove(); }
      var card = input.closest('.mlsr-dcard');
      if (card && !card.querySelector('.is-invalid')) card.classList.remove('is-invalid');
    }

    root.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' && ev.target.matches('.mlsr-input:not(textarea)')) {
        ev.preventDefault();
        if (state.started && state.step < lastStep()) next();
      }
      // The show-more control inside a tier card cannot be a real button, so it
      // has to answer the keyboard itself.
      if ((ev.key === 'Enter' || ev.key === ' ') && ev.target.matches('[role="button"][data-act]')) {
        ev.preventDefault();
        ev.stopPropagation();
        ev.target.click();
      }
    });

    // drag and drop onto the logo zone
    root.addEventListener('dragover', function (ev) {
      var z = ev.target.closest('.mlsr-drop');
      if (z) { ev.preventDefault(); z.classList.add('is-over'); }
    });
    root.addEventListener('dragleave', function (ev) {
      var z = ev.target.closest('.mlsr-drop');
      if (z) z.classList.remove('is-over');
    });
    root.addEventListener('drop', function (ev) {
      var z = ev.target.closest('.mlsr-drop');
      if (!z) return;
      ev.preventDefault();
      z.classList.remove('is-over');
      var zin = z.querySelector('[data-mlsr-logofile]');
      takeLogos(ev.dataTransfer && ev.dataTransfer.files, zin ? zin.getAttribute('data-mlsr-logofile') : 'other');
    });

    /* -- logo preview --------------------------------------------------------- */

    // This used to ask logo.clearbit.com, then fall back to favicon services.
    // Clearbit shut that endpoint down, so in practice every customer was
    // shown a 48px favicon labelled "your logo", and the bytes could never be
    // read anyway because those hosts do not send CORS headers. Both problems
    // are server side problems, so a small Worker solves them: it reads the
    // customer's homepage, finds the real logo, measures it, and proxies the
    // bytes back with CORS so they can be attached.
    //
    // With no service configured the whole affordance stays hidden. Showing a
    // favicon and calling it a logo is worse than showing nothing.
    function tryLogoPreview() {
      var wrap = root.querySelector('[data-mlsr-logopreview]');
      if (!wrap) return;
      var host = siteHost();
      var svc = (cfg.logo_service_url || '').replace(/\/+$/, '');
      if (!host || !svc) { wrap.hidden = true; return; }

      var miss = root.querySelector('[data-mlsr-logomiss]');
      function sayMiss(msg) {
        if (!miss) return;
        miss.textContent = msg;
        miss.hidden = !msg;
      }

      var seq = ++logoSeq;
      sayMiss('');
      fetch(svc + '/?domain=' + encodeURIComponent(host), { mode: 'cors' })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (seq !== logoSeq) return;                 // a newer website was typed
          if (!data || !data.ok || !data.logo) {
            wrap.hidden = true;
            // The service explains itself now, so pass that through rather than
            // leaving a blank space the customer has to interpret.
            sayMiss((data && data.message)
              ? data.message + ' Please upload it below.'
              : 'We could not pull a logo from ' + host + '. Please upload it below.');
            return;
          }
          var img = wrap.querySelector('[data-mlsr-logoimg]');
          // Rendered through the proxy too, so the preview and the attachment
          // are provably the same bytes.
          var proxied = svc + '/img?u=' + encodeURIComponent(data.logo.url) +
            '&sig=' + encodeURIComponent(data.logo.sig);
          if (img) img.src = proxied;
          state.data.logoFoundUrl = data.logo.url;
          state.logoFound = { proxied: proxied, meta: data.logo };
          var note = wrap.querySelector('[data-mlsr-logometa]');
          if (note) note.textContent = describeFound(data.logo);
          // The offer is written when the step renders, before this answer has
          // come back, so the heading is corrected here rather than there.
          var title = wrap.querySelector('[data-mlsr-logotitle]');
          if (title) title.textContent = data.logo.kind === 'icon' ? 'Is this right?' : 'Use this one';
          wrap.hidden = false;
          sayMiss('');
          // The left hand column was written before this answer came back, so
          // its "we need your logo" line is corrected now that we have one.
          var note2 = root.querySelector('[data-mlsr-asidelogo]');
          if (note2) {
            note2.textContent = 'Use the logo we found on your site as your main logo, or upload a better one.';
          }
        })
        .catch(function () {
          if (seq !== logoSeq) return;
          wrap.hidden = true;
          sayMiss('We could not reach ' + host + ' just now. Please upload your logo below.');
        });
    }

    // Say plainly what was found. A 180px app icon and a vector wordmark are
    // both "a logo" and only one of them can be printed.
    function describeFound(meta) {
      // When the finder could only turn up a site icon rather than the brand
      // lockup, say so. Offering a square app icon as "your logo" is exactly
      // how this feature lost trust the first time round.
      if (meta.kind === 'icon') {
        return 'This is the icon from your site, not your full logo. Upload your logo if you have it.';
      }
      if (meta.vector) return 'Vector file, scales to any size';
      var px = meta.width && meta.height ? meta.width + ' by ' + meta.height : 'small';
      if (meta.quality === 'high') return px + ', good enough to start from';
      if (meta.quality === 'medium') return px + ', we may need a larger copy later';
      return px + ', too small to print, we will need the original';
    }

    // Turn the found logo into a real File so it rides along on the form.
    // Failure is not fatal: the request still goes, just without the file.
    function fetchFoundLogo() {
      var found = state.logoFound;
      if (!found || state.data.logoSource !== 'site') return Promise.resolve(null);
      return fetch(found.proxied, { mode: 'cors' })
        .then(function (r) { return r.ok ? r.blob() : null; })
        .then(function (blob) {
          if (!blob || !blob.size) return null;
          var m = found.meta;
          var ext = m.vector ? 'svg' : (m.type === 'jpeg' ? 'jpg' : (m.type || 'png'));
          var name = siteHost().replace(/[^a-z0-9.-]/gi, '') + '-logo-from-site.' + ext;
          try { return new File([blob], name, { type: blob.type || 'image/' + ext }); }
          catch (e) { return null; }
        })
        .catch(function () { return null; });
    }

    /* -- draft resume ---------------------------------------------------------- */

    var pendingDraft = readDraft();
    function applyDraft() {
      if (!pendingDraft) return;
      Object.keys(pendingDraft.data).forEach(function (k) { state.data[k] = pendingDraft.data[k]; });
      elDraft.classList.remove('is-shown');
      var s = Math.min(pendingDraft.step || 1, 6);
      state.started = true;
      pendingDraft = null;
      exportToTray();
      goTo(s);
    }
    if (pendingDraft) {
      elDraft.innerHTML = '<div class="mlsr-draft__c">You have an unfinished store request' +
        (pendingDraft.data.companyName ? ' for <strong>' + esc(pendingDraft.data.companyName) + '</strong>' : '') + '.</div>' +
        '<button type="button" data-act="draft-resume">Pick up where I left off</button>' +
        '<button type="button" data-act="draft-discard">Start over</button>';
      elDraft.classList.add('is-shown');
    }

    /* -- boot ------------------------------------------------------------------ */

    importFromTray();
    root.setAttribute('data-step', '1');
    root.setAttribute('data-mode', 'self');
    render();
    // After a fallback post the page has navigated, so the success screen has
    // to be restored here rather than in the submit handler.
    if (resumeAfterFallback()) {
      elDraft.classList.remove('is-shown');
      pendingDraft = null;
    }
    root.__mlsr = { state: state, goTo: goTo, render: render };
  }

  function boot() {
    document.querySelectorAll('[data-mlsr-root]').forEach(function (root) {
      if (root.__mlsrBound) return;
      root.__mlsrBound = true;
      try { Wizard(root); } catch (e) {
        if (window.console) console.error('[mlsr] wizard failed to start', e);
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();